import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  Divider,
  Chip,
  IconButton,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Slide,
  Avatar
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Mic as MicIcon,
  Psychology as PsychologyIcon,
  MedicalServices as MedicalIcon,
  MonitorHeart as VitalsIcon,
  Assignment as DiagnosisIcon,
  Medication as MedicationIcon,
  Draw as SignIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  Healing as HealingIcon,
  Description as DescriptionIcon,
  Biotech as BiotechIcon,
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarMonthIcon,
  Route as RouteIcon,
  Vaccines as VaccinesIcon,
  Notes as NotesFieldIcon,
} from '@mui/icons-material';
import { NaraSimpleTypewriter, NaraTerminalCard, monoStyle } from './NaraTerminal';
import ConsultaVozStreaming from './ConsultaVozStreaming';
import ExploracionFisica from './ExploracionFisica';
import NotaEvolucionPanel from './NotaEvolucionPanel';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import PrintIcon from '@mui/icons-material/Print';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { exportConsultaPDF } from './exportConsultaPDF';
import OrdenesTab from './OrdenesTab';
import ResultadosEstudiosPanel from './ResultadosEstudiosPanel';
import { FRECUENCIAS_MEDICAMENTOS } from './frecuenciasMedicamentos';

const API_URL = '/api';

// Transición para diálogos
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Panel de Tab
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Estilo común para TextFields
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    background: '#f8fafc',
    '&:hover': { background: '#f1f5f9' },
    '&.Mui-focused': { background: '#fff' }
  }
};

// Estilo premium para inputs en compositor de receta — altura fija 44px
const inputElegante = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    background: '#fff',
    height: 44,
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    '& fieldset': { borderColor: '#e5e7eb' },
    '&:hover fieldset': { borderColor: '#a78bfa' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.12)',
    },
    '&.Mui-focused fieldset': { borderColor: '#8b5cf6', borderWidth: 1.5 },
  },
  '& .MuiOutlinedInput-input': {
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  '& .MuiInputLabel-root': {
    fontWeight: 600,
    color: '#475569',
    '&.Mui-focused': { color: '#7c3aed' },
  },
};

// Variante para textareas multilinea (no aplica height fijo)
const inputEleganteMulti = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    background: '#fff',
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    '& fieldset': { borderColor: '#e5e7eb' },
    '&:hover fieldset': { borderColor: '#a78bfa' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.12)',
    },
    '&.Mui-focused fieldset': { borderColor: '#8b5cf6', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 600,
    color: '#475569',
    '&.Mui-focused': { color: '#7c3aed' },
  },
};

// Componente de sección con título
const SeccionFormulario = ({ icon, titulo, color = "#6366f1", children }) => (
  <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
    <Typography sx={{
      fontWeight: 600,
      color: "#1e3a5f",
      fontSize: 14,
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
      {titulo}
    </Typography>
    {children}
  </Box>
);

const ConsultaForm = ({
  open,
  onClose,
  expedienteId,
  paciente,
  userId,
  onConsultaCreated,
  datosIniciales,
  citaId
}) => {
  const [tabActual, setTabActual] = useState(0);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [consultaId, setConsultaId] = useState(null);
  const [vozStreamingOpen, setVozStreamingOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // NARA Post-Consulta
  const [naraSugerencias, setNaraSugerencias] = useState(null);
  const [loadingNara, setLoadingNara] = useState(false);
  const [showNaraSugerencias, setShowNaraSugerencias] = useState(false);

  // Estado inicial del formulario (reutilizable para reset)
  const FORM_DATA_INICIAL = {
    tipo_consulta: 'primera_vez',
    motivo_consulta: '',
    padecimiento_actual: '',
    interrogatorio_aparatos_sistemas: {},
    exploracion_fisica: {},
    signos_vitales: {
      presion_arterial_sistolica: '',
      presion_arterial_diastolica: '',
      frecuencia_cardiaca: '',
      frecuencia_respiratoria: '',
      temperatura: '',
      peso: '',
      talla: '',
      saturacion_oxigeno: ''
    },
    impresion_diagnostica: '',
    estudios_solicitados: '',
    interconsultas_solicitadas: '',
    plan_tratamiento: '',
    pronostico: '',
    indicaciones_paciente: ''
  };
  const [formData, setFormData] = useState(FORM_DATA_INICIAL);

  // Diagnósticos
  const [diagnosticos, setDiagnosticos] = useState([]);
  const [busquedaCie10, setBusquedaCie10] = useState('');
  const [resultadosCie10, setResultadosCie10] = useState([]);
  const [buscandoCie10, setBuscandoCie10] = useState(false);
  const [codigoLibre, setCodigoLibre] = useState('');
  const [descripcionLibre, setDescripcionLibre] = useState('');

  // Auto-guardado en borrador
  const [autoGuardadoMsg, setAutoGuardadoMsg] = useState('');
  const autoSaveTimer = useRef(null);
  const borradorCargado = useRef(false);

  // Tracking de IDs originales (para eliminar al guardar lo que el usuario quitó)
  const diagsOriginalesIdsRef = useRef(new Set());
  const recetasOriginalesIdsRef = useRef(new Set());

  // Nota de evolución (tipo_consulta === 'subsecuente')
  const [contextoEvolucion, setContextoEvolucion] = useState(null);
  const [cargandoContextoEvolucion, setCargandoContextoEvolucion] = useState(false);
  const [evolucionProblemas, setEvolucionProblemas] = useState({
    problemas: [],
    adherencia_tratamiento: null,
    adherencia_motivo: '',
    evolucion_general: '',
  });

  // Medicamentos
  const [medicamentos, setMedicamentos] = useState([]);
  const [ordenesConsulta, setOrdenesConsulta] = useState([]);
  const [nuevoMedicamento, setNuevoMedicamento] = useState({
    nombre: '',
    presentacion: '',
    dosis: '',
    via: 'oral',
    frecuencia: '',
    duracion: '',
    indicaciones: ''
  });

  // Vademécum - Búsqueda de medicamentos
  const [busquedaMedicamento, setBusquedaMedicamento] = useState('');
  const [resultadosVademecum, setResultadosVademecum] = useState([]);
  const [buscandoVademecum, setBuscandoVademecum] = useState(false);
  const [medicamentoSeleccionado, setMedicamentoSeleccionado] = useState(null);

  // Medicamentos favoritos del doctor (pinneados + frecuentes con tracking automático)
  const [medicamentosFavoritos, setMedicamentosFavoritos] = useState([]);

  // Sugerencias IA
  const [sugerenciasIA, setSugerenciasIA] = useState([]);
  const [cargandoIA, setCargandoIA] = useState(false);

  const tabLabels = ['Motivo', 'Exploración', 'Impresión diagnóstica', 'Laboratorios / Interconsulta', 'Tratamiento', 'Receta', 'Finalizar'];

  // Calcular IMC automáticamente
  useEffect(() => {
    const peso = parseFloat(formData.signos_vitales.peso);
    const talla = parseFloat(formData.signos_vitales.talla);
    if (peso > 0 && talla > 0) {
      const tallaM = talla / 100;
      const imc = (peso / (tallaM * tallaM)).toFixed(1);
      setFormData(prev => ({
        ...prev,
        signos_vitales: { ...prev.signos_vitales, imc }
      }));
    }
  }, [formData.signos_vitales.peso, formData.signos_vitales.talla]);

  // Pre-llenar datos desde Consulta por Voz
  useEffect(() => {
    if (open && datosIniciales) {
      const filtrarNulos = (obj) => {
        if (!obj) return {};
        const limpio = {};
        Object.entries(obj).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') limpio[k] = String(v);
        });
        return limpio;
      };

      // Si la BD trae tension_arterial combinada ("120/80"), descomponer
      const sigVitalesEntrada = filtrarNulos(datosIniciales.signos_vitales);
      if (
        sigVitalesEntrada.tension_arterial &&
        !sigVitalesEntrada.presion_arterial_sistolica &&
        !sigVitalesEntrada.presion_arterial_diastolica
      ) {
        const m = String(sigVitalesEntrada.tension_arterial).match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
        if (m) {
          sigVitalesEntrada.presion_arterial_sistolica = m[1];
          sigVitalesEntrada.presion_arterial_diastolica = m[2];
        }
      }

      // Cargar evolución de problemas si viene en datosIniciales
      if (datosIniciales.evolucion_problemas && typeof datosIniciales.evolucion_problemas === 'object') {
        setEvolucionProblemas({
          problemas: datosIniciales.evolucion_problemas.problemas || [],
          adherencia_tratamiento: datosIniciales.evolucion_problemas.adherencia_tratamiento || null,
          adherencia_motivo: datosIniciales.evolucion_problemas.adherencia_motivo || '',
          evolucion_general: datosIniciales.evolucion_problemas.evolucion_general || '',
        });
      }

      setFormData(prev => ({
        ...prev,
        tipo_consulta: datosIniciales.tipo_consulta || prev.tipo_consulta,
        motivo_consulta: datosIniciales.motivo_consulta || prev.motivo_consulta,
        padecimiento_actual: datosIniciales.padecimiento_actual || prev.padecimiento_actual,
        signos_vitales: { ...prev.signos_vitales, ...sigVitalesEntrada },
        exploracion_fisica: datosIniciales.exploracion_fisica || prev.exploracion_fisica,
        interrogatorio_aparatos_sistemas: datosIniciales.interrogatorio_aparatos_sistemas || prev.interrogatorio_aparatos_sistemas,
        impresion_diagnostica: datosIniciales.impresion_diagnostica || prev.impresion_diagnostica,
        estudios_solicitados: datosIniciales.estudios_solicitados || prev.estudios_solicitados,
        interconsultas_solicitadas: datosIniciales.interconsultas_solicitadas || prev.interconsultas_solicitadas,
        plan_tratamiento: datosIniciales.plan_tratamiento || prev.plan_tratamiento,
        pronostico: datosIniciales.pronostico || prev.pronostico,
        indicaciones_paciente: datosIniciales.indicaciones_paciente || prev.indicaciones_paciente
      }));

      // Diagnósticos existentes (modo edición — incluye id, tipo, certeza, temporalidad)
      if (datosIniciales.diagnosticos_existentes?.length) {
        setDiagnosticos(datosIniciales.diagnosticos_existentes);
        diagsOriginalesIdsRef.current = new Set(
          datosIniciales.diagnosticos_existentes.filter(d => d.id).map(d => d.id)
        );
      } else if (datosIniciales.diagnosticos_sugeridos?.length) {
        setDiagnosticos(datosIniciales.diagnosticos_sugeridos.map((d, i) => ({
          codigo_cie10: d.codigo_cie10 || '',
          descripcion_cie10: d.descripcion || d.descripcion_cie10 || '',
          tipo_diagnostico: i === 0 ? 'principal' : 'secundario',
          certeza: 'presuntivo',
          temporalidad: 'primera_vez',
        })));
      }

      // Órdenes existentes (modo edición)
      if (datosIniciales.ordenes_existentes?.length) {
        setOrdenesConsulta(datosIniciales.ordenes_existentes);
      }

      // Medicamentos existentes (modo edición)
      if (datosIniciales.medicamentos_existentes?.length) {
        setMedicamentos(datosIniciales.medicamentos_existentes);
        recetasOriginalesIdsRef.current = new Set(
          datosIniciales.medicamentos_existentes.filter(m => m._receta_id).map(m => m._receta_id)
        );
      }

      // MODO EDICIÓN: si viene _editandoConsultaId, hacemos PUT en vez de crear nueva
      if (datosIniciales._editandoConsultaId) {
        setConsultaId(datosIniciales._editandoConsultaId);
      }
    }
  }, [open, datosIniciales]);

  // Cargar borrador existente al abrir
  useEffect(() => {
    if (!open || !expedienteId || datosIniciales || borradorCargado.current) return;
    const cargarBorrador = async () => {
      try {
        const res = await fetch(`${API_URL}/emr/expedientes/${expedienteId}/borrador`, {
          headers: { 'X-User-Id': userId }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.borrador) {
          const b = data.borrador;
          setConsultaId(b.id);
          setFormData(prev => ({
            ...prev,
            tipo_consulta: b.tipo_consulta || prev.tipo_consulta,
            motivo_consulta: b.motivo_consulta || '',
            padecimiento_actual: b.padecimiento_actual || '',
            interrogatorio_aparatos_sistemas: b.interrogatorio_aparatos_sistemas || {},
            exploracion_fisica: b.exploracion_fisica || {},
            signos_vitales: b.signos_vitales || prev.signos_vitales,
            impresion_diagnostica: b.impresion_diagnostica || '',
            estudios_solicitados: b.estudios_solicitados || '',
            interconsultas_solicitadas: b.interconsultas_solicitadas || '',
            plan_tratamiento: b.plan_tratamiento || '',
            pronostico: b.pronostico || '',
            indicaciones_paciente: b.indicaciones_paciente || '',
          }));
          setAutoGuardadoMsg('Borrador recuperado');
          setTimeout(() => setAutoGuardadoMsg(''), 3000);
        }
      } catch (err) {
        console.error('Error cargando borrador:', err);
      }
      borradorCargado.current = true;
    };
    cargarBorrador();
  }, [open, expedienteId, userId, datosIniciales]);

  // Cargar contexto de evolución cuando tipo_consulta === 'subsecuente'
  useEffect(() => {
    if (!open || !expedienteId) return;
    if (formData.tipo_consulta !== 'subsecuente') {
      setContextoEvolucion(null);
      return;
    }
    let cancelado = false;
    setCargandoContextoEvolucion(true);
    (async () => {
      try {
        const url = new URL(`${API_URL}/emr/expedientes/${expedienteId}/contexto-evolucion`);
        if (consultaId) url.searchParams.set('excluir_consulta_id', String(consultaId));
        const res = await fetch(url.toString(), { headers: { 'X-User-Id': userId } });
        if (!res.ok) {
          console.warn('[EVOLUCION] No se pudo obtener contexto:', res.status);
          if (!cancelado) setContextoEvolucion({ ultima_consulta: null, problemas_activos: [] });
          return;
        }
        const data = await res.json();
        if (!cancelado) setContextoEvolucion(data);
      } catch (e) {
        console.error('[EVOLUCION] Error fetch contexto:', e);
        if (!cancelado) setContextoEvolucion({ ultima_consulta: null, problemas_activos: [] });
      } finally {
        if (!cancelado) setCargandoContextoEvolucion(false);
      }
    })();
    return () => { cancelado = true; };
  }, [open, expedienteId, userId, formData.tipo_consulta, consultaId]);

  // Cargar diagnósticos, órdenes y medicamentos cuando hay consultaId (modo edición o tras crear borrador)
  useEffect(() => {
    if (!open || !consultaId) return;
    let cancelado = false;
    (async () => {
      try {
        const [rD, rO, rR] = await Promise.all([
          fetch(`${API_URL}/emr/consultas/${consultaId}/diagnosticos`, { headers: { 'X-User-Id': userId } }),
          fetch(`${API_URL}/emr/consultas/${consultaId}/ordenes`, { headers: { 'X-User-Id': userId } }),
          fetch(`${API_URL}/emr/consultas/${consultaId}/recetas`, { headers: { 'X-User-Id': userId } }),
        ]);
        if (cancelado) return;
        if (rD.ok) {
          const d = await rD.json();
          const diagsArr = Array.isArray(d) ? d : (d.diagnosticos || []);
          if (diagsArr.length > 0) {
            setDiagnosticos(diagsArr.map(diag => ({
              id: diag.id,
              codigo_cie10: diag.codigo_cie10,
              descripcion_cie10: diag.descripcion_cie10,
              tipo_diagnostico: diag.tipo_diagnostico || 'principal',
              certeza: diag.certeza || 'presuntivo',
              temporalidad: diag.temporalidad || 'primera_vez',
            })));
            diagsOriginalesIdsRef.current = new Set(diagsArr.map(d2 => d2.id).filter(Boolean));
          }
        }
        if (cancelado) return;
        if (rO.ok) {
          const d = await rO.json();
          const ordenesArr = Array.isArray(d) ? d : (d.ordenes || []);
          if (ordenesArr.length > 0) setOrdenesConsulta(ordenesArr);
        }
        if (rR.ok) {
          const d = await rR.json();
          const recetasArr = Array.isArray(d) ? d : (d.recetas || []);
          if (recetasArr.length > 0) {
            const meds = [];
            recetasArr.forEach(rx => {
              const lista = Array.isArray(rx.medicamentos) ? rx.medicamentos : [];
              lista.forEach(m => meds.push({ ...m, _existente: true, _receta_id: rx.id }));
            });
            recetasOriginalesIdsRef.current = new Set(recetasArr.map(rx => rx.id).filter(Boolean));
            if (meds.length > 0) setMedicamentos(prev => {
              // Solo agregar los que no están ya cargados
              const existingIds = new Set(prev.filter(p => p._existente).map(p => p._receta_id + '_' + p.nombre));
              const nuevos = meds.filter(m => !existingIds.has(m._receta_id + '_' + m.nombre));
              return [...prev, ...nuevos];
            });
          }
        }
      } catch (e) { console.error('[CONSULTA] Error cargando ordenes/recetas:', e); }
    })();
    return () => { cancelado = true; };
  }, [open, consultaId, userId]);

  // Resetear al cerrar — limpia todo el estado para evitar arrastre entre consultas
  useEffect(() => {
    if (!open) {
      borradorCargado.current = false;
      diagsOriginalesIdsRef.current = new Set();
      recetasOriginalesIdsRef.current = new Set();
      setConsultaId(null);
      setFormData(FORM_DATA_INICIAL);
      setDiagnosticos([]);
      setMedicamentos([]);
      setOrdenesConsulta([]);
      setTabActual(0);
      setBusquedaCie10('');
      setResultadosCie10([]);
      setAutoGuardadoMsg('');
      setContextoEvolucion(null);
      setEvolucionProblemas({
        problemas: [], adherencia_tratamiento: null, adherencia_motivo: '', evolucion_general: '',
      });
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-guardado con debounce 3s
  useEffect(() => {
    if (!open || !expedienteId) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      const tieneContenido = formData.motivo_consulta || formData.padecimiento_actual ||
        formData.plan_tratamiento || formData.impresion_diagnostica ||
        Object.values(formData.exploracion_fisica || {}).some(v => v) ||
        Object.values(formData.signos_vitales || {}).some(v => v && v !== '');
      if (!tieneContenido) return;

      try {
        let id = consultaId;
        if (!id) {
          const res = await fetch(`${API_URL}/emr/consultas/borrador`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
            body: JSON.stringify({
              expediente_id: expedienteId,
              medico_id: userId,
              tipo_consulta: formData.tipo_consulta,
              cita_id: citaId || null,
            })
          });
          if (res.ok) {
            const data = await res.json();
            id = data.id;
            setConsultaId(id);
          }
        }
        if (id) {
          // Combinar TA en auto-save también
          const sigVitalesAuto = { ...formData.signos_vitales };
          if (sigVitalesAuto.presion_arterial_sistolica && sigVitalesAuto.presion_arterial_diastolica) {
            sigVitalesAuto.tension_arterial = `${sigVitalesAuto.presion_arterial_sistolica}/${sigVitalesAuto.presion_arterial_diastolica}`;
          }
          const res = await fetch(`${API_URL}/emr/consultas/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
            body: JSON.stringify({
              motivo_consulta: formData.motivo_consulta || null,
              padecimiento_actual: formData.padecimiento_actual || null,
              interrogatorio_aparatos_sistemas: formData.interrogatorio_aparatos_sistemas,
              exploracion_fisica: formData.exploracion_fisica,
              signos_vitales: sigVitalesAuto,
              impresion_diagnostica: formData.impresion_diagnostica || null,
              estudios_solicitados: formData.estudios_solicitados || null,
              interconsultas_solicitadas: formData.interconsultas_solicitadas || null,
              plan_tratamiento: formData.plan_tratamiento || null,
              pronostico: formData.pronostico || null,
              indicaciones_paciente: formData.indicaciones_paciente || null,
            })
          });
          if (res.ok) {
            setAutoGuardadoMsg('Borrador guardado');
            setTimeout(() => setAutoGuardadoMsg(''), 2000);
          }
        }
      } catch (err) {
        console.error('Auto-guardado falló:', err);
      }
    }, 3000);

    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [open, expedienteId, userId, consultaId, formData]);

  // Buscar CIE-10
  const buscarCie10 = useCallback(async (query) => {
    if (query.length < 1) {
      setResultadosCie10([]);
      return;
    }
    setBuscandoCie10(true);
    try {
      const res = await fetch(`${API_URL}/emr/cie10/buscar?q=${encodeURIComponent(query)}&limit=15`, {
        headers: { 'X-User-Id': userId }
      });
      if (res.ok) {
        const data = await res.json();
        setResultadosCie10(data.resultados || []);
      }
    } catch (err) {
      console.error('Error buscando CIE-10:', err);
    } finally {
      setBuscandoCie10(false);
    }
  }, [userId]);

  // Debounce para búsqueda CIE-10
  useEffect(() => {
    const timer = setTimeout(() => {
      if (busquedaCie10) {
        buscarCie10(busquedaCie10);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [busquedaCie10, buscarCie10]);

  // Buscar en Vademécum
  const buscarVademecum = useCallback(async (query) => {
    if (query.length < 2) {
      setResultadosVademecum([]);
      return;
    }
    setBuscandoVademecum(true);
    try {
      const res = await fetch(`${API_URL}/emr/vademecum/buscar?q=${encodeURIComponent(query)}&limit=15`, {
        headers: { 'X-User-Id': userId }
      });
      if (res.ok) {
        const data = await res.json();
        setResultadosVademecum(data.medicamentos || []);
      }
    } catch (err) {
      console.error('Error buscando en vademécum:', err);
    } finally {
      setBuscandoVademecum(false);
    }
  }, [userId]);

  // Debounce para búsqueda vademécum
  useEffect(() => {
    const timer = setTimeout(() => {
      if (busquedaMedicamento) {
        buscarVademecum(busquedaMedicamento);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [busquedaMedicamento, buscarVademecum]);

  // Cuando se selecciona un medicamento del vademécum
  const handleSelectMedicamento = (medicamento) => {
    if (medicamento) {
      setMedicamentoSeleccionado(medicamento);
      setNuevoMedicamento({
        ...nuevoMedicamento,
        medicamento_id: medicamento.id,  // FK para tracking de favoritos
        nombre: `${medicamento.nombre_generico} (${medicamento.nombre_comercial})`,
        presentacion: medicamento.forma_farmaceutica || '',
        dosis: medicamento.concentracion || '',
        via: medicamento.via_administracion?.toLowerCase()?.includes('oral') ? 'oral' :
             medicamento.via_administracion?.toLowerCase()?.includes('intra') ? 'intramuscular' :
             medicamento.via_administracion?.toLowerCase()?.includes('topic') ? 'topica' :
             medicamento.via_administracion?.toLowerCase()?.includes('oftal') ? 'oftalmica' :
             medicamento.via_administracion?.toLowerCase()?.includes('inhal') ? 'inhalada' : 'oral'
      });
      setBusquedaMedicamento('');
      setResultadosVademecum([]);
    }
  };

  // Cargar medicamentos favoritos del doctor
  const cargarMedicamentosFavoritos = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/emr/medicamentos-favoritos?limit=20`, {
        headers: { 'X-User-Id': userId }
      });
      if (res.ok) {
        const data = await res.json();
        setMedicamentosFavoritos(Array.isArray(data) ? data : []);
      }
    } catch (e) { /* silently */ }
  }, [userId]);

  useEffect(() => { cargarMedicamentosFavoritos(); }, [cargarMedicamentosFavoritos]);

  // Click en un chip de favoritos: aplica smart defaults (editables después)
  const handleSelectFavorito = (fav) => {
    setMedicamentoSeleccionado({
      id: fav.medicamento_id,
      nombre_generico: fav.nombre_medicamento.split(' (')[0],
      nombre_comercial: (fav.nombre_medicamento.match(/\(([^)]+)\)/) || [])[1] || '',
      forma_farmaceutica: fav.last_presentacion,
      concentracion: fav.last_dosis,
      via_administracion: fav.last_via,
    });
    setNuevoMedicamento({
      medicamento_id: fav.medicamento_id,
      nombre: fav.nombre_medicamento,
      presentacion: fav.last_presentacion || '',
      dosis: fav.last_dosis || '',
      via: fav.last_via || 'oral',
      frecuencia: fav.last_frecuencia || '',
      duracion: fav.last_duracion || '',
      indicaciones: fav.last_indicaciones || '',
    });
    setBusquedaMedicamento('');
    setResultadosVademecum([]);
  };

  // Pin / Unpin un medicamento como favorito manual
  const togglePinMedicamento = async (medicamentoId, isPinnedActual) => {
    try {
      await fetch(`${API_URL}/emr/medicamentos-favoritos/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          medicamento_id: medicamentoId,
          is_pinned: !isPinnedActual,
        }),
      });
      cargarMedicamentosFavoritos();
    } catch (e) { /* silently */ }
  };

  const isFavorito = (medicamentoId) =>
    medicamentosFavoritos.some(f => f.medicamento_id === medicamentoId && f.is_pinned);

  // Agregar diagnóstico
  const agregarDiagnostico = (cie10) => {
    if (diagnosticos.find(d => d.codigo_cie10 === cie10.codigo && d.descripcion_cie10 === cie10.descripcion)) {
      setSnackbar({ open: true, message: 'Este diagnóstico ya fue agregado', severity: 'warning' });
      return;
    }
    setDiagnosticos([...diagnosticos, {
      codigo_cie10: cie10.codigo,
      descripcion_cie10: cie10.descripcion,
      tipo_diagnostico: diagnosticos.length === 0 ? 'principal' : 'secundario',
      certeza: 'presuntivo',
      temporalidad: 'primera_vez'
    }]);
    setBusquedaCie10('');
    setResultadosCie10([]);
  };

  // Obtener sugerencias de IA
  const obtenerSugerenciasIA = async () => {
    if (!formData.motivo_consulta && !formData.padecimiento_actual) {
      setSnackbar({ open: true, message: 'Ingresa el motivo de consulta primero', severity: 'warning' });
      return;
    }

    setCargandoIA(true);
    try {
      const sintomas = (formData.motivo_consulta + ' ' + formData.padecimiento_actual)
        .split(/[,.\n]/)
        .map(s => s.trim())
        .filter(s => s.length > 2);

      const res = await fetch(`${API_URL}/emr/ia/sugerir-diagnosticos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({
          sintomas,
          edad: calcularEdad(paciente?.fecha_nacimiento),
          sexo: paciente?.sexo
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSugerenciasIA(data.sugerencias || []);
      }
    } catch (err) {
      console.error('Error obteniendo sugerencias IA:', err);
      setSnackbar({ open: true, message: 'Error al obtener sugerencias', severity: 'error' });
    } finally {
      setCargandoIA(false);
    }
  };

  // Agregar medicamento
  const agregarMedicamento = () => {
    if (!nuevoMedicamento.nombre || !nuevoMedicamento.dosis) {
      setSnackbar({ open: true, message: 'Nombre y dosis son requeridos', severity: 'warning' });
      return;
    }
    setMedicamentos([...medicamentos, { ...nuevoMedicamento }]);
    setNuevoMedicamento({
      medicamento_id: null,
      nombre: '',
      presentacion: '',
      dosis: '',
      via: 'oral',
      frecuencia: '',
      duracion: '',
      indicaciones: ''
    });
    setMedicamentoSeleccionado(null);
    setBusquedaMedicamento('');
  };

  // Guardar consulta
  const guardarConsulta = async (finalizar = false) => {
    setGuardando(true);
    const log = (etapa, info = {}) => console.log(`[CONSULTA-GUARDADO] ${etapa}`, info);
    log('inicio', { finalizar, consultaIdActual: consultaId, expedienteId, userId });

    try {
      // Combinar sistólica/diastólica en un solo campo "tension_arterial: 120/80"
      const sigVitales = { ...formData.signos_vitales };
      const sis = sigVitales.presion_arterial_sistolica;
      const dia = sigVitales.presion_arterial_diastolica;
      if (sis && dia) {
        sigVitales.tension_arterial = `${sis}/${dia}`;
      } else if (sigVitales.tension_arterial) {
        // Si solo está el combinado, mantenerlo
      } else {
        delete sigVitales.tension_arterial;
      }

      const consultaData = {
        expediente_id: expedienteId,
        medico_id: userId,
        cita_id: citaId || null,
        tipo_consulta: formData.tipo_consulta,
        motivo_consulta: formData.motivo_consulta,
        padecimiento_actual: formData.padecimiento_actual,
        interrogatorio_aparatos_sistemas: formData.interrogatorio_aparatos_sistemas,
        exploracion_fisica: formData.exploracion_fisica,
        signos_vitales: sigVitales,
        impresion_diagnostica: formData.impresion_diagnostica,
        estudios_solicitados: formData.estudios_solicitados,
        interconsultas_solicitadas: formData.interconsultas_solicitadas,
        plan_tratamiento: formData.plan_tratamiento,
        pronostico: formData.pronostico,
        indicaciones_paciente: formData.indicaciones_paciente,
        evolucion_problemas: formData.tipo_consulta === 'subsecuente' ? evolucionProblemas : {},
      };

      let res;
      if (consultaId) {
        log('PUT existente', { consultaId, motivo: consultaData.motivo_consulta });
        res = await fetch(`${API_URL}/emr/consultas/${consultaId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
          body: JSON.stringify(consultaData)
        });
      } else {
        log('POST nueva', { motivo: consultaData.motivo_consulta });
        res = await fetch(`${API_URL}/emr/consultas`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
          body: JSON.stringify(consultaData)
        });
      }

      if (!res.ok) {
        const errBody = await res.text();
        log('❌ Error principal', { status: res.status, body: errBody });
        throw new Error(`No se pudo guardar la consulta (HTTP ${res.status}): ${errBody.substring(0, 200)}`);
      }

      const consultaGuardada = await res.json();
      log('✅ Consulta principal guardada', { id: consultaGuardada.id, estado: consultaGuardada.estado });
      setConsultaId(consultaGuardada.id);

      // 1) Eliminar diagnósticos que el usuario quitó del array
      const idsActuales = new Set(diagnosticos.map(d => d.id).filter(Boolean));
      const idsAEliminar = [...diagsOriginalesIdsRef.current].filter(id => !idsActuales.has(id));
      for (const idEliminar of idsAEliminar) {
        const r = await fetch(`${API_URL}/emr/diagnosticos/${idEliminar}`, {
          method: 'DELETE',
          headers: { 'X-User-Id': userId }
        });
        if (!r.ok) log('⚠️ No se pudo eliminar diagnóstico', { id: idEliminar, status: r.status });
      }
      if (idsAEliminar.length > 0) log('🗑️ Diagnósticos eliminados', { count: idsAEliminar.length });

      // 2) Crear los nuevos diagnósticos (los que no tienen id)
      const diagsErrores = [];
      for (const diag of diagnosticos) {
        if (!diag.id) {
          const r = await fetch(`${API_URL}/emr/consultas/${consultaGuardada.id}/diagnosticos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
            body: JSON.stringify(diag)
          });
          if (!r.ok) {
            const e = await r.text();
            log('❌ Error diagnóstico', { diag: diag.descripcion_cie10, status: r.status, body: e });
            diagsErrores.push(diag.descripcion_cie10);
          }
        }
      }
      if (diagsErrores.length > 0) throw new Error(`No se guardaron ${diagsErrores.length} diagnóstico(s): ${diagsErrores.join(', ')}`);
      if (diagnosticos.length > 0) log('✅ Diagnósticos guardados', { count: diagnosticos.length });

      // 3) Recetas: si hay recetas originales, las eliminamos todas y recreamos una nueva
      //    con los medicamentos actuales (esto soporta borrar/editar meds).
      const tieneOriginales = recetasOriginalesIdsRef.current.size > 0;
      const stripMed = (m) => {
        const { _existente, _receta_id, ...rest } = m;
        return rest;
      };
      if (tieneOriginales) {
        for (const recetaId of recetasOriginalesIdsRef.current) {
          const r = await fetch(`${API_URL}/emr/recetas/${recetaId}`, {
            method: 'DELETE',
            headers: { 'X-User-Id': userId }
          });
          if (!r.ok) log('⚠️ No se pudo eliminar receta', { id: recetaId, status: r.status });
        }
        log('🗑️ Recetas originales eliminadas', { count: recetasOriginalesIdsRef.current.size });
        recetasOriginalesIdsRef.current = new Set();

        if (medicamentos.length > 0) {
          const r = await fetch(`${API_URL}/emr/consultas/${consultaGuardada.id}/recetas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
            body: JSON.stringify({
              medicamentos: medicamentos.map(stripMed),
              indicaciones_generales: formData.indicaciones_paciente
            })
          });
          if (!r.ok) {
            const e = await r.text();
            log('❌ Error receta (re-creación)', { status: r.status, body: e });
            throw new Error(`No se pudo guardar la receta (HTTP ${r.status})`);
          }
          log('✅ Receta re-creada con meds actuales', { count: medicamentos.length });
        }
      } else {
        // Caso sin recetas originales: solo crear con los meds (todos serán nuevos)
        const medsNuevos = medicamentos.filter(m => !m._existente).map(stripMed);
        if (medsNuevos.length > 0) {
          const r = await fetch(`${API_URL}/emr/consultas/${consultaGuardada.id}/recetas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
            body: JSON.stringify({ medicamentos: medsNuevos, indicaciones_generales: formData.indicaciones_paciente })
          });
          if (!r.ok) {
            const e = await r.text();
            log('❌ Error receta', { status: r.status, body: e });
            throw new Error(`No se pudo guardar la receta (HTTP ${r.status})`);
          }
          log('✅ Receta guardada', { meds_nuevos: medsNuevos.length });
        }
      }

      // Finalizar si se solicita
      if (finalizar) {
        const r = await fetch(`${API_URL}/emr/consultas/${consultaGuardada.id}/finalizar`, {
          method: 'POST', headers: { 'X-User-Id': userId }
        });
        if (!r.ok) {
          const e = await r.text();
          log('❌ Error finalizar', { status: r.status, body: e });
          throw new Error(`No se pudo finalizar (HTTP ${r.status}): ${e.substring(0, 200)}`);
        }
        log('✅ Finalizada');
      }

      log('🎉 Todo OK', { id: consultaGuardada.id });
      setSnackbar({
        open: true,
        message: finalizar
          ? `✅ Consulta #${consultaGuardada.id} finalizada y guardada correctamente`
          : `✅ Consulta #${consultaGuardada.id} guardada como borrador`,
        severity: 'success'
      });

      if (finalizar && onConsultaCreated) {
        onConsultaCreated(consultaGuardada);
        // Fetch NARA post-consulta suggestions instead of closing immediately
        setLoadingNara(true);
        fetch(`${API_URL}/emr/ia/post-consulta/${consultaGuardada.id}`, {
          method: 'POST',
          headers: { 'X-User-Id': userId }
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data && data.sugerencias && data.sugerencias.length > 0) {
              setNaraSugerencias(data);
              setLoadingNara(false);
            } else {
              setLoadingNara(false);
              onClose();
            }
          })
          .catch(() => { setLoadingNara(false); onClose(); });
      }

    } catch (err) {
      console.error('[CONSULTA-GUARDADO] ❌ ERROR FINAL:', err);
      setSnackbar({
        open: true,
        message: `❌ Error al guardar: ${err.message || 'Error desconocido'}`,
        severity: 'error'
      });
    } finally {
      setGuardando(false);
    }
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
    return edad;
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#f8fafc",
          boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
          border: "none",
          overflow: "hidden",
          minHeight: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header con gradiente */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          color: "#fff",
          py: 2,
          px: 3
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <MedicalIcon sx={{ fontSize: 26, color: "#d97706" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: 0.5 }}>
              Nueva Consulta Médica
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Avatar sx={{ width: 20, height: 20, bgcolor: '#fbbf24', fontSize: 10 }}>
                {paciente?.nombre_completo?.charAt(0)}
              </Avatar>
              <Typography sx={{ fontSize: 13, opacity: 0.9 }}>
                {paciente?.nombre_completo} • {calcularEdad(paciente?.fecha_nacimiento)} años • {paciente?.sexo}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {autoGuardadoMsg && (
            <Chip
              label={autoGuardadoMsg}
              size="small"
              icon={<CheckIcon sx={{ fontSize: 14, color: '#d1fae5 !important' }} />}
              sx={{
                background: 'rgba(16, 185, 129, 0.3)',
                color: '#d1fae5',
                fontWeight: 600,
                fontSize: 10,
                animation: 'fadeIn 0.3s',
              }}
            />
          )}
          <Chip
            label={`Paso ${tabActual + 1} de 7`}
            size="small"
            sx={{
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 11
            }}
          />
          <IconButton
            onClick={onClose}
            sx={{
              color: '#fff',
              '&:hover': { background: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Tabs estilizados */}
        <Box sx={{
          borderBottom: "1px solid #e2e8f0",
          background: "#fff",
          px: 2
        }}>
          <Tabs
            value={tabActual}
            onChange={(e, v) => setTabActual(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                minHeight: 56,
                color: '#64748b',
                '&.Mui-selected': {
                  color: '#1e3a5f'
                }
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab icon={<MedicalIcon sx={{ fontSize: 20 }} />} label="Motivo" iconPosition="start" />
            <Tab icon={<VitalsIcon sx={{ fontSize: 20 }} />} label="Exploración" iconPosition="start" />
            <Tab icon={<DiagnosisIcon sx={{ fontSize: 20 }} />} label="Impresión diagnóstica" iconPosition="start" />
            <Tab icon={<BiotechIcon sx={{ fontSize: 20 }} />} label="Laboratorios / Interconsulta" iconPosition="start" />
            <Tab icon={<HealingIcon sx={{ fontSize: 20 }} />} label="Tratamiento" iconPosition="start" />
            <Tab icon={<MedicationIcon sx={{ fontSize: 20 }} />} label="Receta" iconPosition="start" />
            <Tab icon={<CheckIcon sx={{ fontSize: 20 }} />} label="Finalizar" iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 220px)' }}>
          {/* Tab 0: Motivo de consulta */}
          <TabPanel value={tabActual} index={0}>
            {/* Panel de Nota de Evolución (solo para tipo subsecuente) */}
            {formData.tipo_consulta === 'subsecuente' && (
              <Box sx={{ mb: 2 }}>
                <SeccionFormulario icon={<MedicalIcon />} titulo="Nota de Evolución" color="#6366f1">
                  <NotaEvolucionPanel
                    contexto={contextoEvolucion}
                    cargandoContexto={cargandoContextoEvolucion}
                    evolucion={evolucionProblemas}
                    onChange={setEvolucionProblemas}
                    onAplicarConciliacion={(medsPrevios, conciliacion) => {
                      // Agregar medicamentos "continuar" y "ajustar" a la receta del padre
                      const aIncorporar = [];
                      medsPrevios.forEach((med, idx) => {
                        const dec = conciliacion[idx];
                        if (!dec || !dec.accion) return;
                        if (dec.accion === 'suspender') return;
                        // Limpiar refs internos para evitar mezclar con _existente
                        const { _existente, _receta_id, ...limpio } = med;
                        const indicaciones = dec.accion === 'ajustar' && dec.motivo
                          ? `${med.indicaciones || ''} ${med.indicaciones ? '· ' : ''}Ajuste: ${dec.motivo}`.trim()
                          : (med.indicaciones || '');
                        aIncorporar.push({ ...limpio, indicaciones });
                      });
                      // Filtrar duplicados por nombre con los meds ya en pantalla
                      setMedicamentos(prev => {
                        const nombresExistentes = new Set(prev.map(m => (m.nombre || '').toLowerCase().trim()));
                        const nuevos = aIncorporar.filter(m =>
                          !nombresExistentes.has((m.nombre || '').toLowerCase().trim())
                        );
                        return [...prev, ...nuevos];
                      });
                      setSnackbar({
                        open: true,
                        message: `✅ ${aIncorporar.length} medicamento(s) incorporados al plan. Revisalos en la pestaña Receta.`,
                        severity: 'success',
                      });
                    }}
                  />
                </SeccionFormulario>
              </Box>
            )}
            {formData.tipo_consulta === 'subsecuente' ? (
              /* Modo NOTA DE EVOLUCIÓN — versión compacta:
                 solo tipo (para poder cambiarlo) + motivo (obligatorio) en una fila */
              <SeccionFormulario icon={<MedicalIcon />} titulo="Identificación de la consulta" color="#6366f1">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth select label="Tipo" size="small" sx={textFieldSx}
                      value={formData.tipo_consulta}
                      onChange={(e) => setFormData({ ...formData, tipo_consulta: e.target.value })}
                    >
                      <MenuItem value="primera_vez">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                          Primera vez
                        </Box>
                      </MenuItem>
                      <MenuItem value="subsecuente">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                          Subsecuente
                        </Box>
                      </MenuItem>
                      <MenuItem value="urgencia">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                          Urgencia
                        </Box>
                      </MenuItem>
                      <MenuItem value="interconsulta">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8b5cf6' }} />
                          Interconsulta
                        </Box>
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth size="small" required
                      label="Motivo de la consulta"
                      placeholder="Ej: control de hipertensión, revisión de resultados de laboratorio..."
                      value={formData.motivo_consulta}
                      onChange={(e) => setFormData({ ...formData, motivo_consulta: e.target.value })}
                      sx={textFieldSx}
                    />
                  </Grid>
                </Grid>
                <Typography sx={{ mt: 1, fontSize: 10.5, color: '#64748b', fontStyle: 'italic' }}>
                  💡 La narrativa subjetiva del paciente se registra arriba en <b>"¿Cómo se ha sentido?"</b> del panel de evolución.
                </Typography>
              </SeccionFormulario>
            ) : (
              <>
                <SeccionFormulario icon={<MedicalIcon />} titulo="Tipo de Consulta" color="#6366f1">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Tipo de Consulta"
                        value={formData.tipo_consulta}
                        onChange={(e) => setFormData({ ...formData, tipo_consulta: e.target.value })}
                        size="small"
                        sx={textFieldSx}
                      >
                        <MenuItem value="primera_vez">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                            Primera vez
                          </Box>
                        </MenuItem>
                        <MenuItem value="subsecuente">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                            Subsecuente
                          </Box>
                        </MenuItem>
                        <MenuItem value="urgencia">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                            Urgencia
                          </Box>
                        </MenuItem>
                        <MenuItem value="interconsulta">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8b5cf6' }} />
                            Interconsulta
                          </Box>
                        </MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </SeccionFormulario>

                <SeccionFormulario icon={<DescriptionIcon />} titulo="Motivo de la Consulta" color="#10b981">
                  <TextField
                    fullWidth
                    label="¿Por qué acude el paciente?"
                    multiline
                    rows={3}
                    value={formData.motivo_consulta}
                    onChange={(e) => setFormData({ ...formData, motivo_consulta: e.target.value })}
                    placeholder="Describa el motivo principal de la consulta..."
                    required
                    size="small"
                    sx={textFieldSx}
                  />
                </SeccionFormulario>

                <SeccionFormulario icon={<HealingIcon />} titulo="Padecimiento Actual" color="#f59e0b">
                  <TextField
                    fullWidth
                    label="Historia del padecimiento"
                    multiline
                    rows={5}
                    value={formData.padecimiento_actual}
                    onChange={(e) => setFormData({ ...formData, padecimiento_actual: e.target.value })}
                    placeholder="Descripción cronológica del padecimiento, síntomas, evolución, tratamientos previos..."
                    size="small"
                    sx={textFieldSx}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<MicIcon />}
                      size="small"
                      sx={{
                        borderRadius: 2,
                        borderColor: '#e2e8f0',
                        color: '#64748b',
                        '&:hover': { borderColor: '#6366f1', color: '#6366f1', background: '#f1f5f9' }
                      }}
                    >
                      Grabar Audio
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={cargandoIA ? <CircularProgress size={16} /> : <PsychologyIcon />}
                      onClick={obtenerSugerenciasIA}
                      disabled={cargandoIA}
                      size="small"
                      sx={{
                        borderRadius: 2,
                        borderColor: '#10b981',
                        color: '#10b981',
                        '&:hover': { borderColor: '#059669', background: '#f0fdf4' }
                      }}
                    >
                      Sugerencias IA
                    </Button>
                  </Box>
                </SeccionFormulario>
              </>
            )}

            <SeccionFormulario icon={<SearchIcon />} titulo="Interrogatorio por Aparatos y Sistemas" color="#0ea5e9">
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={16}
                size="small"
                value={
                  typeof formData.interrogatorio_aparatos_sistemas === 'string'
                    ? formData.interrogatorio_aparatos_sistemas
                    : (formData.interrogatorio_aparatos_sistemas?.texto || '')
                }
                onChange={(e) => setFormData({
                  ...formData,
                  interrogatorio_aparatos_sistemas: { texto: e.target.value },
                })}
                placeholder={
                  'Registra los hallazgos del interrogatorio por aparatos y sistemas. Por ejemplo:\n\n' +
                  '• Cardiovascular: niega dolor precordial, palpitaciones, disnea, edemas.\n' +
                  '• Respiratorio: niega tos, expectoración, disnea, hemoptisis.\n' +
                  '• Digestivo: niega dolor abdominal, náusea, vómito, alteraciones del hábito intestinal.\n' +
                  '• Urinario: niega disuria, polaquiuria, hematuria.\n' +
                  '• Osteomuscular: niega artralgias, mialgias, limitación de movimiento.\n' +
                  '• Neurológico: niega cefalea, mareo, convulsiones, debilidad focal.\n' +
                  '• Endocrino: niega polidipsia, poliuria, cambios de peso.\n' +
                  '• Hematológico: niega sangrados, petequias, adenopatías.\n' +
                  '• Psicológico: niega alteraciones del sueño, estado de ánimo o ansiedad.'
                }
                InputLabelProps={{ shrink: true }}
                sx={{
                  ...textFieldSx,
                  '& .MuiInputBase-input::placeholder': {
                    color: '#94a3b8',
                    opacity: 1,
                    fontSize: 13,
                    lineHeight: 1.6,
                  },
                }}
              />
            </SeccionFormulario>
          </TabPanel>

          {/* Tab 1: Exploración física */}
          <TabPanel value={tabActual} index={1}>
            <SeccionFormulario icon={<VitalsIcon />} titulo="Signos Vitales" color="#ef4444">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {/* TA: dos campos separados por / con badge clínico en vivo */}
                {(() => {
                  const sis = parseInt(formData.signos_vitales.presion_arterial_sistolica) || null;
                  const dia = parseInt(formData.signos_vitales.presion_arterial_diastolica) || null;
                  let badge = null;
                  if (sis && dia) {
                    if (sis < 90 || dia < 60) badge = { label: 'Hipotensión', bg: '#dbeafe', color: '#1d4ed8' };
                    else if (sis >= 140 || dia >= 90) badge = { label: 'Hipertensión', bg: '#fee2e2', color: '#b91c1c' };
                    else if (sis >= 130 || dia >= 80) badge = { label: 'Elevada', bg: '#fef3c7', color: '#a16207' };
                    else badge = { label: 'Normal', bg: '#dcfce7', color: '#15803d' };
                  }
                  return (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ef4444', mb: 0.3, letterSpacing: 0.3 }}>TA</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <TextField
                          size="small"
                          placeholder="120"
                          value={formData.signos_vitales.presion_arterial_sistolica || ''}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 3);
                            setFormData({ ...formData, signos_vitales: { ...formData.signos_vitales, presion_arterial_sistolica: v } });
                          }}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 600, fontSize: 14, padding: '6px 2px' } }}
                          sx={{ width: 52, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444', borderWidth: 2 } } }}
                        />
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#94a3b8' }}>/</Typography>
                        <TextField
                          size="small"
                          placeholder="80"
                          value={formData.signos_vitales.presion_arterial_diastolica || ''}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 3);
                            setFormData({ ...formData, signos_vitales: { ...formData.signos_vitales, presion_arterial_diastolica: v } });
                          }}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 600, fontSize: 14, padding: '6px 2px' } }}
                          sx={{ width: 52, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444', borderWidth: 2 } } }}
                        />
                        <Typography sx={{ fontSize: 9, color: '#94a3b8', ml: 0.3 }}>mmHg</Typography>
                      </Box>
                      {badge && (
                        <Box sx={{
                          mt: 0.5,
                          display: 'inline-block',
                          px: 0.7, py: 0.1,
                          borderRadius: 1,
                          background: badge.bg,
                          color: badge.color,
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          textTransform: 'uppercase',
                        }}>
                          {badge.label}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                {[
                  { label: 'FC', unit: 'lpm', key: 'frecuencia_cardiaca', width: 90, max: 300 },
                  { label: 'FR', unit: 'rpm', key: 'frecuencia_respiratoria', width: 90, max: 80 },
                  { label: 'Temp', unit: '°C', key: 'temperatura', width: 90, step: 0.1, max: 45, decimal: true },
                  { label: 'Peso', unit: 'kg', key: 'peso', width: 90, step: 0.1, max: 500, decimal: true },
                  { label: 'Talla', unit: 'cm', key: 'talla', width: 90, max: 250 },
                  { label: 'IMC', unit: 'kg/m²', width: 90, readOnly: true, value: formData.signos_vitales.imc || '—', bg: '#f0fdf4' },
                  { label: 'SpO₂', unit: '%', key: 'saturacion_oxigeno', width: 90, max: 100 },
                ].map((f) => (
                  <Box key={f.label} sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ef4444', mb: 0.3, letterSpacing: 0.3 }}>
                      {f.label}
                    </Typography>
                    <TextField
                      type={f.type || 'number'}
                      size="small"
                      placeholder={f.placeholder || ''}
                      value={f.value !== undefined ? f.value : (formData.signos_vitales[f.key] || '')}
                      onChange={f.onChange ? (e) => f.onChange(e.target.value) : f.readOnly ? undefined : (e) => {
                        let v = e.target.value;
                        if (f.decimal) {
                          v = v.replace(/[^0-9.]/g, '');
                          const parts = v.split('.');
                          if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
                        } else {
                          v = v.replace(/[^0-9]/g, '');
                        }
                        if (f.max && parseFloat(v) > f.max) return;
                        setFormData({ ...formData, signos_vitales: { ...formData.signos_vitales, [f.key]: v } });
                      }}
                      inputProps={{
                        style: { textAlign: 'center', fontWeight: 600, fontSize: 14, padding: '6px 4px' },
                        step: f.step || 1,
                        readOnly: f.readOnly || false,
                        inputMode: f.inputMode || 'numeric',
                        pattern: f.decimal ? '[0-9]*\\.?[0-9]*' : '[0-9]*',
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 9, color: '#94a3b8' }}>{f.unit}</Typography></InputAdornment>,
                      }}
                      sx={{
                        width: f.width,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          background: f.bg || '#fff',
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444', borderWidth: 2 },
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </SeccionFormulario>

            <SeccionFormulario icon={<PersonIcon />} titulo="Exploración Física" color="#8b5cf6">
              <ExploracionFisica
                data={formData.exploracion_fisica}
                onChange={(newData) => setFormData({ ...formData, exploracion_fisica: newData })}
              />
            </SeccionFormulario>
          </TabPanel>

          {/* Tab 2: Diagnóstico */}
          <TabPanel value={tabActual} index={2}>
            {/* === Asistente IA para Impresión Diagnóstica === */}
            <Box sx={{ p: 2, pb: 0 }}>
              <AsistenteDiagnosticoIA
                userId={userId}
                diagnosticosActuales={diagnosticos}
                contexto={{
                  edad: paciente?.fecha_nacimiento
                    ? Math.floor((Date.now() - new Date(paciente.fecha_nacimiento).getTime()) / 31557600000)
                    : null,
                  sexo: paciente?.sexo || null,
                  motivo_consulta: formData.motivo_consulta,
                  padecimiento_actual: formData.padecimiento_actual,
                  interrogatorio: formData.interrogatorio_aparatos_sistemas,
                  signos_vitales: formData.signos_vitales,
                  exploracion_fisica: formData.exploracion_fisica,
                  impresion_diagnostica_actual: formData.impresion_diagnostica,
                }}
                onAceptar={(sug) => {
                  // Verificar duplicado
                  if (diagnosticos.find(d => d.codigo_cie10 === sug.codigo_cie10)) {
                    setSnackbar({ open: true, message: 'Ese diagnóstico ya está agregado', severity: 'warning' });
                    return;
                  }
                  // Si se sugiere "principal" pero ya hay uno, agregarlo como secundario
                  const yaHayPrincipal = diagnosticos.some(d => d.tipo_diagnostico === 'principal');
                  const tipo = (sug.tipo_sugerido === 'principal' && !yaHayPrincipal)
                    ? 'principal' : 'secundario';
                  setDiagnosticos([...diagnosticos, {
                    codigo_cie10: sug.codigo_cie10,
                    descripcion_cie10: sug.descripcion,
                    tipo_diagnostico: tipo,
                    certeza: 'presuntivo',
                    temporalidad: 'primera_vez',
                  }]);
                  setSnackbar({
                    open: true,
                    message: `✅ Agregado ${sug.codigo_cie10} como ${tipo}`,
                    severity: 'success',
                  });
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0 }}>
              {/* Panel izquierdo: Búsqueda */}
              <Box sx={{ flex: 1, borderRight: '1px solid #e2e8f0' }}>
                <SeccionFormulario icon={<SearchIcon />} titulo="Búsqueda CIE-10" color="#3b82f6">
                  <TextField
                    fullWidth
                    label="Buscar diagnóstico"
                    size="small"
                    value={busquedaCie10}
                    onChange={(e) => setBusquedaCie10(e.target.value)}
                    placeholder="Escriba código o descripción..."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                      endAdornment: buscandoCie10 && <CircularProgress size={18} />
                    }}
                    sx={textFieldSx}
                  />
                  <Box sx={{ maxHeight: 250, overflowY: 'auto', mt: 2 }}>
                    {resultadosCie10.map((cie) => (
                      <Box
                        key={cie.codigo}
                        onClick={() => agregarDiagnostico(cie)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: 2,
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            background: '#f1f5f9',
                            borderColor: '#6366f1',
                            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={cie.codigo}
                            size="small"
                            sx={{
                              bgcolor: '#e0e7ff',
                              color: '#4338ca',
                              fontWeight: 600,
                              fontSize: 11
                            }}
                          />
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1e3a5f' }}>
                            {cie.descripcion_corta || cie.descripcion}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Diagnóstico libre (cuando no se encuentra en el catálogo) */}
                  {busquedaCie10.length >= 2 && resultadosCie10.length === 0 && !buscandoCie10 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#fef3c7', borderRadius: 2, border: '1px dashed #f59e0b' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#92400e', mb: 1 }}>
                        "{busquedaCie10}" no se encontró en el catálogo CIE-10
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          agregarDiagnostico({
                            codigo: '—',
                            descripcion: busquedaCie10,
                          });
                          setBusquedaCie10('');
                        }}
                        sx={{
                          borderRadius: 2, fontWeight: 600, fontSize: 12, textTransform: 'none',
                          background: '#f59e0b', '&:hover': { background: '#d97706' },
                        }}
                      >
                        Agregar como impresión diagnóstica
                      </Button>
                    </Box>
                  )}

                  {/* Sugerencias IA */}
                  {sugerenciasIA.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#166534', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PsychologyIcon sx={{ fontSize: 16 }} />
                        Sugerencias IA
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#166534', mb: 1.5 }}>
                        Basadas en los síntomas descritos. Confirme con su criterio médico.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sugerenciasIA.map((sug, idx) => (
                          <Chip
                            key={idx}
                            label={`${sug.codigo_cie10}: ${sug.descripcion}`}
                            onClick={() => agregarDiagnostico({ codigo: sug.codigo_cie10, descripcion: sug.descripcion })}
                            size="small"
                            sx={{
                              cursor: 'pointer',
                              bgcolor: sug.probabilidad === 'alta' ? '#dcfce7' : sug.probabilidad === 'media' ? '#fef9c3' : '#f1f5f9',
                              color: sug.probabilidad === 'alta' ? '#166534' : sug.probabilidad === 'media' ? '#854d0e' : '#475569',
                              fontSize: 11,
                              '&:hover': { opacity: 0.8 }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </SeccionFormulario>
              </Box>

              {/* Panel derecho: Diagnósticos agregados */}
              <Box sx={{ flex: 1 }}>
                <SeccionFormulario icon={<DiagnosisIcon />} titulo={`Diagnósticos (${diagnosticos.length})`} color="#10b981">
                  {diagnosticos.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <DiagnosisIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                      <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                        Busque y agregue diagnósticos CIE-10
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {diagnosticos.map((diag, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: idx === 0 ? '#eff6ff' : '#f8fafc',
                            border: `1px solid ${idx === 0 ? '#bfdbfe' : '#e2e8f0'}`,
                          }}
                        >
                          {/* Línea 1: tipo + código + descripción + eliminar */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                              <Chip
                                label={idx === 0 ? 'PRINCIPAL' : 'SECUNDARIO'}
                                size="small"
                                sx={{
                                  height: 20, fontWeight: 700, fontSize: 9, letterSpacing: 0.5,
                                  bgcolor: idx === 0 ? '#1e3a5f' : '#6366f1', color: '#fff',
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                              {diag.codigo_cie10 && diag.codigo_cie10 !== '—' && (
                                <Chip label={diag.codigo_cie10} size="small" sx={{
                                  height: 20, bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600, fontSize: 10,
                                  '& .MuiChip-label': { px: 0.8 },
                                }} />
                              )}
                              <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#1e293b' }}>
                                {diag.descripcion_cie10}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newDiags = diagnosticos.filter((_, i) => i !== idx);
                                if (newDiags.length > 0) newDiags[0].tipo_diagnostico = 'principal';
                                setDiagnosticos(newDiags);
                              }}
                              sx={{ color: '#94a3b8', p: 0.3, '&:hover': { color: '#ef4444' } }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          {/* Línea 2: certeza + temporalidad como chips clickeables */}
                          <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8 }}>
                            <Chip
                              label={diag.certeza === 'definitivo' ? 'Definitivo' : 'Presuntivo'}
                              size="small"
                              onClick={() => {
                                const newDiags = [...diagnosticos];
                                newDiags[idx].certeza = diag.certeza === 'definitivo' ? 'presuntivo' : 'definitivo';
                                setDiagnosticos(newDiags);
                              }}
                              sx={{
                                height: 22, cursor: 'pointer', fontWeight: 600, fontSize: 10,
                                bgcolor: diag.certeza === 'definitivo' ? '#dcfce7' : '#fef9c3',
                                color: diag.certeza === 'definitivo' ? '#166534' : '#854d0e',
                                border: `1px solid ${diag.certeza === 'definitivo' ? '#bbf7d0' : '#fde68a'}`,
                                '& .MuiChip-label': { px: 1 },
                                '&:hover': { opacity: 0.8 },
                              }}
                            />
                            <Chip
                              label={(diag.temporalidad || 'primera_vez') === 'subsecuente' ? 'Subsecuente' : 'Primera vez'}
                              size="small"
                              onClick={() => {
                                const newDiags = [...diagnosticos];
                                newDiags[idx].temporalidad = (diag.temporalidad || 'primera_vez') === 'subsecuente' ? 'primera_vez' : 'subsecuente';
                                setDiagnosticos(newDiags);
                              }}
                              sx={{
                                height: 22, cursor: 'pointer', fontWeight: 600, fontSize: 10,
                                bgcolor: (diag.temporalidad || 'primera_vez') === 'subsecuente' ? '#e0e7ff' : '#f0fdf4',
                                color: (diag.temporalidad || 'primera_vez') === 'subsecuente' ? '#3730a3' : '#065f46',
                                border: `1px solid ${(diag.temporalidad || 'primera_vez') === 'subsecuente' ? '#c7d2fe' : '#bbf7d0'}`,
                                '& .MuiChip-label': { px: 1 },
                                '&:hover': { opacity: 0.8 },
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </SeccionFormulario>

                <SeccionFormulario icon={<DescriptionIcon />} titulo="Impresión Diagnóstica" color="#f59e0b">
                  <TextField
                    fullWidth
                    label="Resumen diagnóstico"
                    multiline
                    rows={3}
                    size="small"
                    value={formData.impresion_diagnostica}
                    onChange={(e) => setFormData({ ...formData, impresion_diagnostica: e.target.value })}
                    placeholder="Resumen de la impresión diagnóstica..."
                    sx={textFieldSx}
                  />
                </SeccionFormulario>
              </Box>
            </Box>
          </TabPanel>

          {/* Tab 3: Órdenes y documentos */}
          <TabPanel value={tabActual} index={3}>
            {/* === Asistente IA Estudios/Interconsultas === */}
            <Box sx={{ mb: 2 }}>
              <AsistenteEstudiosIA
                userId={userId}
                consultaId={consultaId}
                ordenes={ordenesConsulta}
                onOrdenesChange={setOrdenesConsulta}
                API_URL={API_URL}
                contexto={{
                  edad: paciente?.fecha_nacimiento
                    ? Math.floor((Date.now() - new Date(paciente.fecha_nacimiento).getTime()) / 31557600000)
                    : null,
                  sexo: paciente?.sexo || null,
                  motivo_consulta: formData.motivo_consulta,
                  padecimiento_actual: formData.padecimiento_actual,
                  interrogatorio: formData.interrogatorio_aparatos_sistemas,
                  signos_vitales: formData.signos_vitales,
                  exploracion_fisica: formData.exploracion_fisica,
                  impresion_diagnostica_actual: formData.impresion_diagnostica,
                  diagnosticos: diagnosticos.map(d => ({
                    codigo_cie10: d.codigo_cie10,
                    descripcion_cie10: d.descripcion_cie10,
                    tipo_diagnostico: d.tipo_diagnostico,
                  })),
                }}
                onSnackbar={(msg, severity) => setSnackbar({ open: true, message: msg, severity })}
              />
            </Box>

            {/* Resultados traídos por el paciente (solo nota de evolución) */}
            {formData.tipo_consulta === 'subsecuente' && (
              <Box sx={{ mb: 2 }}>
                <SeccionFormulario icon={<BiotechIcon />} titulo="Resultados de estudios del paciente" color="#0891b2">
                  <ResultadosEstudiosPanel
                    estudios={evolucionProblemas?.estudios_revisados || []}
                    expedienteId={expedienteId}
                    onChange={(nuevosEstudios) => setEvolucionProblemas({
                      ...evolucionProblemas,
                      estudios_revisados: nuevosEstudios,
                    })}
                    contextoClinico={[
                      formData.motivo_consulta,
                      contextoEvolucion?.ultima_consulta?.diagnostico_principal
                        ? `Dx previo: ${contextoEvolucion.ultima_consulta.diagnostico_principal.descripcion}`
                        : null,
                    ].filter(Boolean).join(' · ')}
                  />
                </SeccionFormulario>
              </Box>
            )}

            <OrdenesTab
              consultaId={consultaId}
              userId={userId}
              ordenes={ordenesConsulta}
              onOrdenesChange={setOrdenesConsulta}
              API_URL={API_URL}
              paciente={paciente}
            />
          </TabPanel>

          {/* Tab 4: Tratamiento */}
          <TabPanel value={tabActual} index={4}>
            {/* === Asistente IA Tratamiento === */}
            <Box sx={{ mb: 2 }}>
              <AsistenteTratamientoIA
                userId={userId}
                planTratamientoActual={formData.plan_tratamiento || ''}
                onLlenarPlanTratamiento={(texto) =>
                  setFormData({ ...formData, plan_tratamiento: texto })
                }
                pronosticoActual={formData.pronostico || ''}
                onEstablecerPronostico={(p) =>
                  setFormData({ ...formData, pronostico: p })
                }
                indicacionesActuales={formData.indicaciones_paciente || ''}
                onUsarIndicaciones={(texto) =>
                  setFormData({ ...formData, indicaciones_paciente: texto })
                }
                contexto={{
                  edad: paciente?.fecha_nacimiento
                    ? Math.floor((Date.now() - new Date(paciente.fecha_nacimiento).getTime()) / 31557600000)
                    : null,
                  sexo: paciente?.sexo || null,
                  peso: formData.signos_vitales?.peso || null,
                  imc: formData.signos_vitales?.imc || null,
                  motivo_consulta: formData.motivo_consulta,
                  padecimiento_actual: formData.padecimiento_actual,
                  signos_vitales: formData.signos_vitales,
                  impresion_diagnostica_actual: formData.impresion_diagnostica,
                  diagnosticos: diagnosticos.map(d => ({
                    codigo_cie10: d.codigo_cie10,
                    descripcion_cie10: d.descripcion_cie10,
                    tipo_diagnostico: d.tipo_diagnostico,
                  })),
                  medicamentos_previos: medicamentos.map(m => ({
                    nombre: m.nombre, dosis: m.dosis, via: m.via, frecuencia: m.frecuencia,
                  })),
                }}
                onSnackbar={(msg, severity) => setSnackbar({ open: true, message: msg, severity })}
              />
            </Box>
            <SeccionFormulario icon={<HealingIcon />} titulo="Plan de Tratamiento" color="#6366f1">
              <TextField
                fullWidth
                label="Indicaciones terapéuticas"
                multiline
                rows={4}
                size="small"
                value={formData.plan_tratamiento}
                onChange={(e) => setFormData({ ...formData, plan_tratamiento: e.target.value })}
                placeholder="Tratamiento farmacológico y no farmacológico, estudios solicitados, referencias..."
                sx={textFieldSx}
              />
            </SeccionFormulario>

            <SeccionFormulario icon={<DiagnosisIcon />} titulo="Pronóstico" color="#8b5cf6">
              <TextField
                fullWidth
                label="Pronóstico del paciente"
                multiline
                rows={2}
                size="small"
                value={formData.pronostico}
                onChange={(e) => setFormData({ ...formData, pronostico: e.target.value })}
                placeholder="Bueno, reservado, malo..."
                sx={textFieldSx}
              />
            </SeccionFormulario>

          </TabPanel>

          {/* Tab 5: Receta */}
          <TabPanel value={tabActual} index={5}>
            {/* === HEADER === */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
              }}>
                <MedicationIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                  Receta médica
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  Componé a la izquierda · La receta se arma a la derecha
                </Typography>
              </Box>
            </Box>

            {/* === FAVORITOS — Fila full width estable === */}
            {medicamentosFavoritos.length > 0 && (
              <Box sx={{
                mb: 2, p: 1.2, borderRadius: 2,
                bgcolor: 'rgba(254, 243, 199, 0.4)',
                border: '1px solid #fde68a',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.6 }}>
                  <StarIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                  <Typography sx={{
                    fontSize: 11, fontWeight: 700,
                    color: '#92400e', letterSpacing: 0.6,
                    textTransform: 'uppercase',
                  }}>
                    Mis frecuentes
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#94a3b8', ml: 0.5 }}>
                    Click para usar · Estrella para fijar
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                  {medicamentosFavoritos.slice(0, 12).map((fav) => {
                    const nombreCorto = (fav.nombre_medicamento || '').split(' (')[0];
                    return (
                      <Box
                        key={fav.id}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3,
                          pl: 1, pr: 0.3, py: 0.3,
                          borderRadius: 999,
                          bgcolor: fav.is_pinned ? '#fef3c7' : '#f1f5f9',
                          border: `1px solid ${fav.is_pinned ? '#fbbf24' : '#cbd5e1'}`,
                          transition: 'all 0.15s',
                          '&:hover': {
                            bgcolor: fav.is_pinned ? '#fde68a' : '#e2e8f0',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                          },
                        }}
                      >
                        <Box
                          onClick={() => handleSelectFavorito(fav)}
                          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5 }}
                        >
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: fav.is_pinned ? '#92400e' : '#475569', whiteSpace: 'nowrap' }}>
                            {nombreCorto}
                          </Typography>
                          {fav.last_dosis && (
                            <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>
                              · {fav.last_dosis}
                            </Typography>
                          )}
                          {fav.count_use > 0 && (
                            <Typography sx={{ fontSize: 9.5, color: '#94a3b8', fontWeight: 700, px: 0.4, py: 0.05, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.7)' }}>
                              ×{fav.count_use}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); togglePinMedicamento(fav.medicamento_id, fav.is_pinned); }}
                          sx={{
                            p: 0.2,
                            color: fav.is_pinned ? '#f59e0b' : '#cbd5e1',
                            '&:hover': { color: fav.is_pinned ? '#dc2626' : '#f59e0b' },
                          }}
                        >
                          {fav.is_pinned ? <StarIcon sx={{ fontSize: 13 }} /> : <StarBorderIcon sx={{ fontSize: 13 }} />}
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* === TWO-PANE LAYOUT: Compositor (izq) + Receta actual (der) === */}
            <Grid container spacing={2} sx={{ alignItems: 'flex-start', flexWrap: { md: 'nowrap' } }}>

            <Grid item xs={12} md={7} sx={{ minWidth: 0, flexBasis: { md: '58.333%' }, maxWidth: { md: '58.333%' } }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fafbff 0%, #f5f3ff 100%)',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                overflow: 'hidden',
                width: '100%',
                boxSizing: 'border-box',
              }}>
                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, color: '#6366f1',
                  textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.2,
                }}>
                  Compositor
                </Typography>

                {/* === Autocomplete con popper flotante (no empuja layout) === */}
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={resultadosVademecum}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.nombre_generico
                      ? `${option.nombre_generico}${option.nombre_comercial ? ` (${option.nombre_comercial})` : ''}`
                      : '';
                  }}
                  inputValue={busquedaMedicamento}
                  onInputChange={(_, v, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setBusquedaMedicamento(v);
                      if (!v) {
                        setNuevoMedicamento({ ...nuevoMedicamento, nombre: '', medicamento_id: null });
                        setMedicamentoSeleccionado(null);
                      } else if (!medicamentoSeleccionado) {
                        setNuevoMedicamento({ ...nuevoMedicamento, nombre: v });
                      }
                    }
                  }}
                  onChange={(_, value) => {
                    if (typeof value === 'object' && value !== null) {
                      handleSelectMedicamento(value);
                    }
                  }}
                  loading={buscandoVademecum}
                  loadingText="Buscando..."
                  noOptionsText={busquedaMedicamento && busquedaMedicamento.length >= 2
                    ? 'Ninguno encontrado · podés tipear libremente'
                    : 'Tipeá al menos 2 letras'}
                  renderOption={(props, option) => {
                    const fav = isFavorito(option.id);
                    return (
                      <Box component="li" {...props} key={option.id} sx={{ display: 'flex !important', alignItems: 'flex-start', gap: 1, p: 1.2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <MedicationIcon sx={{ fontSize: 18, color: '#f59e0b', flexShrink: 0 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>
                              {option.nombre_generico}
                            </Typography>
                            {option.nombre_comercial && (
                              <Chip label={option.nombre_comercial} size="small"
                                sx={{ height: 20, fontSize: 10, bgcolor: '#e0e7ff', color: '#4338ca' }} />
                            )}
                            {option.controlado && (
                              <Chip label="Controlado" size="small"
                                sx={{ height: 18, fontSize: 9, bgcolor: '#fef2f2', color: '#dc2626' }} />
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.4 }}>
                            {[option.concentracion, option.forma_farmaceutica, option.via_administracion]
                              .filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <Tooltip title={fav ? 'Quitar de favoritos' : 'Marcar como favorito'} placement="left" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); togglePinMedicamento(option.id, fav); }}
                            sx={{
                              p: 0.4,
                              color: fav ? '#f59e0b' : '#cbd5e1',
                              '&:hover': { color: fav ? '#dc2626' : '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.08)' },
                            }}
                          >
                            {fav ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Buscar medicamento"
                      placeholder="Nombre genérico o comercial..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start" sx={{ ml: 0.4 }}>
                              <SearchIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {buscandoVademecum && <CircularProgress size={16} sx={{ color: '#8b5cf6', mr: 0.5 }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={inputElegante}
                    />
                  )}
                  sx={{ mb: 1.5 }}
                />

                <Grid container spacing={1.5}>
                {/* === FORMA Y DOSIS — 2 columnas balanceadas con íconos === */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth select
                    label="Forma farmacéutica"
                    size="small"
                    value={nuevoMedicamento.presentacion}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, presentacion: e.target.value })}
                    SelectProps={{ displayEmpty: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MedicationIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputElegante}
                  >
                    <MenuItem value=""><em style={{ color: '#94a3b8' }}>Sin especificar</em></MenuItem>
                    <MenuItem value="Tableta">Tableta</MenuItem>
                    <MenuItem value="Cápsula">Cápsula</MenuItem>
                    <MenuItem value="Comprimido">Comprimido</MenuItem>
                    <MenuItem value="Jarabe">Jarabe</MenuItem>
                    <MenuItem value="Suspensión">Suspensión</MenuItem>
                    <MenuItem value="Solución">Solución</MenuItem>
                    <MenuItem value="Gotas">Gotas</MenuItem>
                    <MenuItem value="Crema">Crema</MenuItem>
                    <MenuItem value="Pomada">Pomada</MenuItem>
                    <MenuItem value="Gel">Gel</MenuItem>
                    <MenuItem value="Inyectable">Inyectable</MenuItem>
                    <MenuItem value="Ampolleta">Ampolleta</MenuItem>
                    <MenuItem value="Vial">Vial</MenuItem>
                    <MenuItem value="Aerosol">Aerosol</MenuItem>
                    <MenuItem value="Inhalador">Inhalador</MenuItem>
                    <MenuItem value="Supositorio">Supositorio</MenuItem>
                    <MenuItem value="Óvulo">Óvulo</MenuItem>
                    <MenuItem value="Parche">Parche</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Dosis"
                    size="small"
                    value={nuevoMedicamento.dosis}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, dosis: e.target.value })}
                    placeholder="500 mg, 5 ml, 1 cda, 2 puffs..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VaccinesIcon sx={{ fontSize: 18, color: '#0ea5e9' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputElegante}
                  />
                </Grid>

                {/* === POSOLOGÍA — vía + frecuencia + duración con íconos === */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth select
                    label="Vía de administración"
                    size="small"
                    value={nuevoMedicamento.via}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, via: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <RouteIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputElegante}
                  >
                    <MenuItem value="oral">Oral</MenuItem>
                    <MenuItem value="sublingual">Sublingual</MenuItem>
                    <MenuItem value="intramuscular">Intramuscular (IM)</MenuItem>
                    <MenuItem value="intravenosa">Intravenosa (IV)</MenuItem>
                    <MenuItem value="subcutanea">Subcutánea (SC)</MenuItem>
                    <MenuItem value="topica">Tópica</MenuItem>
                    <MenuItem value="oftalmica">Oftálmica</MenuItem>
                    <MenuItem value="otica">Ótica</MenuItem>
                    <MenuItem value="nasal">Nasal</MenuItem>
                    <MenuItem value="inhalada">Inhalada</MenuItem>
                    <MenuItem value="rectal">Rectal</MenuItem>
                    <MenuItem value="vaginal">Vaginal</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Duración del tratamiento"
                    size="small"
                    value={nuevoMedicamento.duracion}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, duracion: e.target.value })}
                    placeholder="7 días, 2 semanas, hasta terminar..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthIcon sx={{ fontSize: 18, color: '#10b981' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputElegante}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CampoFrecuencia
                    valor={nuevoMedicamento.frecuencia}
                    onChange={(v) => setNuevoMedicamento({ ...nuevoMedicamento, frecuencia: v })}
                    inputElegante={inputElegante}
                  />
                </Grid>

                {/* === INDICACIONES ESPECÍFICAS === */}
                <Grid item xs={12}>
                  {/* === Quick-fill chips: indicaciones frecuentes === */}
                  <Box sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.8,
                    alignItems: 'center',
                  }}>
                    <Typography sx={{
                      fontSize: 9.5, fontWeight: 700,
                      color: '#94a3b8', letterSpacing: 0.6,
                      textTransform: 'uppercase', mr: 0.3,
                    }}>
                      Quick-fill
                    </Typography>
                    {[
                      'Con alimentos',
                      'En ayunas',
                      'Antes de dormir',
                      'Tomar con abundante agua',
                      'Mantener refrigerado',
                      'Agitar antes de usar',
                      'Evitar alcohol',
                      'No manejar después',
                      'Suspender si aparece sarpullido',
                      'No interrumpir aunque haya mejoría',
                    ].map((frase) => {
                      const yaIncluida = (nuevoMedicamento.indicaciones || '').includes(frase);
                      return (
                        <Chip
                          key={frase}
                          label={frase}
                          size="small"
                          onClick={() => {
                            if (yaIncluida) return;
                            const actual = (nuevoMedicamento.indicaciones || '').trim();
                            const nuevoTexto = actual ? `${actual} · ${frase}` : frase;
                            setNuevoMedicamento({ ...nuevoMedicamento, indicaciones: nuevoTexto });
                          }}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 500,
                            cursor: yaIncluida ? 'default' : 'pointer',
                            bgcolor: yaIncluida ? '#dcfce7' : '#f1f5f9',
                            color: yaIncluida ? '#15803d' : '#475569',
                            border: `1px solid ${yaIncluida ? '#86efac' : '#e2e8f0'}`,
                            opacity: yaIncluida ? 0.85 : 1,
                            transition: 'all 0.15s',
                            '&:hover': {
                              bgcolor: yaIncluida ? '#dcfce7' : '#e2e8f0',
                              color: yaIncluida ? '#15803d' : '#1e293b',
                              transform: yaIncluida ? 'none' : 'translateY(-1px)',
                              boxShadow: yaIncluida ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
                            },
                            '&:before': yaIncluida ? {
                              content: '"✓ "', mr: 0.2,
                            } : {},
                          }}
                        />
                      );
                    })}
                  </Box>
                  <TextField
                    fullWidth
                    label="Indicaciones específicas (opcional)"
                    multiline
                    rows={5}
                    value={nuevoMedicamento.indicaciones || ''}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, indicaciones: e.target.value })}
                    placeholder="Click un chip para agregar · O escribí tus propias indicaciones..."
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiOutlinedInput-root': {
                        ...(textFieldSx?.['& .MuiOutlinedInput-root'] || {}),
                        alignItems: 'flex-start',
                        padding: 0,
                      },
                      '& .MuiOutlinedInput-root textarea': {
                        resize: 'none',
                        overflow: 'auto !important',
                      },
                    }}
                  />
                </Grid>

                {/* === BOTÓN AGREGAR — full width del compositor === */}
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={agregarMedicamento}
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      height: 44,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: 13.5,
                      letterSpacing: 0.2,
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
                      transition: 'box-shadow 0.15s ease, background 0.15s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        boxShadow: '0 6px 18px rgba(16, 185, 129, 0.45)',
                      }
                    }}
                  >
                    Agregar a la receta
                  </Button>
                </Grid>
              </Grid>
              </Box>
            </Grid>

            {/* === RIGHT PANE: Receta actual === */}
            <Grid item xs={12} md={5} sx={{ minWidth: 0, flexBasis: { md: '41.666%' }, maxWidth: { md: '41.666%' } }}>
              <Box sx={{
                p: 2, borderRadius: 3,
                background: '#fff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                position: { xs: 'static', md: 'sticky' },
                top: { md: 16 },
                maxHeight: { md: 'calc(100vh - 80px)' },
                overflowY: { md: 'auto' },
                overflowX: 'hidden',
                width: '100%',
                boxSizing: 'border-box',
              }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  mb: 1.5, pb: 1.2, borderBottom: '1px solid #e2e8f0',
                }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: 1.5,
                    bgcolor: '#fef3c7', color: '#a16207',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <DescriptionIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                    Receta actual
                  </Typography>
                  <Box sx={{
                    ml: 'auto',
                    px: 1, py: 0.2, borderRadius: 999,
                    bgcolor: medicamentos.length > 0 ? '#fef3c7' : '#f1f5f9',
                    color: medicamentos.length > 0 ? '#92400e' : '#64748b',
                    fontSize: 11, fontWeight: 700,
                  }}>
                    {medicamentos.length} med{medicamentos.length === 1 ? '' : 's'}
                  </Box>
                </Box>

                {/* === Borrador en vivo: lo que se está componiendo === */}
                {nuevoMedicamento.nombre && (
                  <Paper elevation={0} sx={{
                    mb: 1.2, p: 1.3, borderRadius: 2,
                    background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
                    border: '2px dashed #93c5fd',
                    minWidth: 0, overflow: 'hidden',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
                      <Box sx={{
                        width: 7, height: 7, borderRadius: '50%',
                        bgcolor: '#3b82f6',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.4 },
                        },
                      }} />
                      <Typography sx={{
                        fontSize: 9.5, fontWeight: 800,
                        color: '#1e40af', letterSpacing: 0.5,
                        textTransform: 'uppercase',
                      }}>
                        Borrador · sin agregar
                      </Typography>
                    </Box>
                    <Typography sx={{
                      fontWeight: 700, color: '#1e293b', fontSize: 13, lineHeight: 1.3,
                      overflowWrap: 'anywhere',
                    }}>
                      {nuevoMedicamento.nombre}
                      {nuevoMedicamento.presentacion && (
                        <span style={{ fontWeight: 500, color: '#64748b', marginLeft: 4, fontSize: 12 }}>
                          · {nuevoMedicamento.presentacion}
                        </span>
                      )}
                      {nuevoMedicamento.dosis && (
                        <span style={{ fontWeight: 700, color: '#2563eb', marginLeft: 4 }}>
                          {nuevoMedicamento.dosis}
                        </span>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.6 }}>
                      {nuevoMedicamento.via && (
                        <Chip size="small" label={`Vía ${nuevoMedicamento.via}`}
                          sx={{ height: 19, fontSize: 10, background: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }} />
                      )}
                      {nuevoMedicamento.frecuencia && (
                        <Chip size="small" label={`🕐 ${nuevoMedicamento.frecuencia}`}
                          sx={{ height: 19, fontSize: 10, background: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }} />
                      )}
                      {nuevoMedicamento.duracion && (
                        <Chip size="small" label={`⏳ ${nuevoMedicamento.duracion}`}
                          sx={{ height: 19, fontSize: 10, background: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }} />
                      )}
                    </Box>
                    {nuevoMedicamento.indicaciones && (
                      <Typography sx={{
                        mt: 0.5, fontSize: 10.5, color: '#1e3a8a', fontStyle: 'italic',
                        pl: 0.8, borderLeft: '2px solid #93c5fd',
                        overflowWrap: 'anywhere',
                      }}>
                        {nuevoMedicamento.indicaciones}
                      </Typography>
                    )}
                  </Paper>
                )}

                {medicamentos.length === 0 && !nuevoMedicamento.nombre ? (
                  <Box sx={{
                    textAlign: 'center', py: 5, borderRadius: 2,
                    bgcolor: '#fefce8', border: '1px dashed #fcd34d',
                  }}>
                    <MedicationIcon sx={{ fontSize: 40, color: '#fde68a', mb: 1 }} />
                    <Typography sx={{ color: '#a16207', fontSize: 13, fontWeight: 600 }}>
                      Receta vacía
                    </Typography>
                    <Typography sx={{ color: '#d97706', fontSize: 11, mt: 0.3 }}>
                      Componé un medicamento y agregalo aquí
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    {medicamentos.map((med, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 1.3, borderRadius: 2,
                          background: '#fff',
                          border: '1px solid #fcd34d',
                          borderLeft: '4px solid #f59e0b',
                          transition: 'all 0.15s',
                          '&:hover': { boxShadow: '0 2px 12px rgba(245, 158, 11, 0.15)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{
                            width: 26, height: 26, borderRadius: '50%',
                            background: '#fef3c7', color: '#a16207',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, fontWeight: 800, fontSize: 12,
                          }}>
                            {idx + 1}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: 13.5, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                              {med.nombre}
                              {med.presentacion && (
                                <span style={{ fontWeight: 600, color: '#64748b', marginLeft: 4, fontSize: 12 }}>
                                  · {med.presentacion}
                                </span>
                              )}
                              {med.dosis && (
                                <span style={{ fontWeight: 700, color: '#a16207', marginLeft: 4 }}>
                                  {med.dosis}
                                </span>
                              )}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.6 }}>
                              {med.via && (
                                <Chip size="small" label={`Vía ${med.via}`}
                                  sx={{ height: 20, fontSize: 10.5, background: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }} />
                              )}
                              {med.frecuencia && (
                                <Chip size="small" label={`🕐 ${med.frecuencia}`}
                                  sx={{ height: 20, fontSize: 10.5, background: '#fef3c7', color: '#a16207', fontWeight: 600 }} />
                              )}
                              {med.duracion && (
                                <Chip size="small" label={`⏳ ${med.duracion}`}
                                  sx={{ height: 20, fontSize: 10.5, background: '#dcfce7', color: '#15803d', fontWeight: 600 }} />
                              )}
                            </Box>
                            {med.indicaciones && (
                              <Box sx={{
                                mt: 0.6, pl: 1, py: 0.3, borderLeft: '2px solid #fcd34d',
                                background: '#fffbeb', borderRadius: '0 4px 4px 0',
                              }}>
                                <Typography sx={{ fontSize: 11, color: '#78350f', fontStyle: 'italic', overflowWrap: 'anywhere' }}>
                                  {med.indicaciones}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Tooltip title="Quitar de la receta">
                            <IconButton
                              size="small"
                              onClick={() => setMedicamentos(medicamentos.filter((_, i) => i !== idx))}
                              sx={{ color: '#dc2626', flexShrink: 0, p: 0.5 }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>

            </Grid>

            {/* === Indicaciones generales al paciente — full width === */}
            <SeccionFormulario icon={<DescriptionIcon />} titulo="Indicaciones generales al paciente" color="#0891b2">
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={formData.indicaciones_paciente}
                onChange={(e) => setFormData({ ...formData, indicaciones_paciente: e.target.value })}
                placeholder="Dieta, curaciones, uso de faja especial, baño, siguiente cita, reposo, ejercicio, etc."
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </SeccionFormulario>
          </TabPanel>

          {/* Tab 6: Finalizar */}
          <TabPanel value={tabActual} index={6}>
            <SeccionFormulario icon={<WarningIcon />} titulo="Verificación Final" color="#f59e0b">
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 2,
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>
                  Antes de finalizar, verifique:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>Motivo de consulta registrado</li>
                  <li>Signos vitales completos</li>
                  <li>Al menos un diagnóstico CIE-10</li>
                  <li>Plan de tratamiento definido</li>
                </Box>
              </Alert>
            </SeccionFormulario>

            <SeccionFormulario icon={<DiagnosisIcon />} titulo="Resumen de la Consulta" color="#6366f1">
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Tipo</Typography>
                    <Chip
                      label={formData.tipo_consulta.replace('_', ' ')}
                      size="small"
                      sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Diagnósticos</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#10b981' }}>
                      {diagnosticos.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#fef3c7', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Medicamentos</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#d97706' }}>
                      {medicamentos.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Estado</Typography>
                    <Chip
                      label="En proceso"
                      size="small"
                      sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </SeccionFormulario>

            <Box sx={{ px: 3, py: 3, background: '#fff', display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<SaveIcon />}
                onClick={() => guardarConsulta(false)}
                disabled={guardando}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 4,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': { background: '#f1f5f9', borderColor: '#94a3b8' }
                }}
              >
                Guardar Borrador
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<PrintIcon />}
                onClick={async () => {
                  try {
                    // Combinar TA si viene partida
                    const sigVit = { ...formData.signos_vitales };
                    if (sigVit.presion_arterial_sistolica && sigVit.presion_arterial_diastolica) {
                      sigVit.tension_arterial = `${sigVit.presion_arterial_sistolica}/${sigVit.presion_arterial_diastolica}`;
                    }
                    await exportConsultaPDF({
                      paciente,
                      consulta: {
                        ...formData,
                        signos_vitales: sigVit,
                        evolucion_problemas: evolucionProblemas,
                        fecha_consulta: new Date().toISOString(),
                      },
                      diagnosticos,
                      medicamentos,
                      ordenes: ordenesConsulta,
                    });
                  } catch (e) {
                    console.error('[PDF]', e);
                    setSnackbar({ open: true, message: 'Error generando PDF: ' + (e.message || ''), severity: 'error' });
                  }
                }}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 4,
                  borderColor: '#0ea5e9',
                  color: '#0ea5e9',
                  '&:hover': { background: '#f0f9ff', borderColor: '#0284c7' }
                }}
              >
                Imprimir PDF
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={guardando ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                onClick={() => guardarConsulta(true)}
                disabled={guardando || !formData.motivo_consulta}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 4,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)'
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8'
                  }
                }}
              >
                Finalizar Consulta
              </Button>
            </Box>
          </TabPanel>
        </Box>
      </DialogContent>

      {/* Footer con navegación */}
      <DialogActions sx={{
        px: 3,
        py: 2,
        borderTop: "1px solid #e2e8f0",
        background: "#fff",
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            borderColor: "#e2e8f0",
            color: "#64748b",
            "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" }
          }}
        >
          Cancelar
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            disabled={tabActual === 0}
            onClick={() => setTabActual(tabActual - 1)}
            startIcon={<PrevIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: '#64748b',
              '&:hover': { background: '#f1f5f9' }
            }}
          >
            Anterior
          </Button>
          <Button
            variant="contained"
            disabled={tabActual === 6}
            onClick={() => setTabActual(tabActual + 1)}
            endIcon={<NextIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #2d5a87 0%, #3d6a97 100%)' },
              '&:disabled': { background: '#e2e8f0', color: '#94a3b8' }
            }}
          >
            Siguiente
          </Button>
        </Box>
      </DialogActions>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 10000 : 5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ borderRadius: 2, fontSize: 14, fontWeight: 600, boxShadow: 6 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* NARA Post-Consulta Overlay */}
      {(loadingNara || naraSugerencias) && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(15, 23, 42, 0.92)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1400,
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <Box sx={{ width: { xs: '90%', sm: 520 }, maxWidth: '100%' }}>
            <NaraTerminalCard
              label="nara@post-consulta ~ sugerencias"
              loading={loadingNara}
              loadingText="Analizando consulta"
              sx={{ mb: 0 }}
            >
              {naraSugerencias && (
                <Box>
                  <NaraSimpleTypewriter
                    text={naraSugerencias.resumen}
                    onComplete={() => setShowNaraSugerencias(true)}
                  />
                  {showNaraSugerencias && naraSugerencias.sugerencias && (
                    <Box sx={{
                      mt: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)',
                      '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                      animation: 'fadeIn 0.3s ease-out'
                    }}>
                      {naraSugerencias.sugerencias.map((sug, idx) => {
                        const tipoColors = {
                          seguimiento: '#0ea5e9',
                          whatsapp: '#25D366',
                          laboratorio: '#f59e0b',
                          alerta: '#ef4444'
                        };
                        const color = tipoColors[sug.tipo] || '#64748b';
                        return (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                            <Chip
                              label={sug.tipo}
                              size="small"
                              sx={{
                                ...monoStyle, fontSize: 8.5, fontWeight: 700, height: 18,
                                bgcolor: `${color}22`,
                                color: color,
                                border: `1px solid ${color}44`,
                                minWidth: 75, mt: 0.2
                              }}
                            />
                            <Typography sx={{
                              ...monoStyle,
                              fontSize: 11, color: '#cbd5e1', lineHeight: 1.5, flex: 1
                            }}>
                              {sug.texto}
                            </Typography>
                            {sug.prioridad === 'alta' && (
                              <WarningIcon sx={{ fontSize: 14, color: '#ef4444', mt: 0.3, flexShrink: 0 }} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </NaraTerminalCard>

            {/* Botón cerrar */}
            {(naraSugerencias && showNaraSugerencias) && (
              <Box sx={{ textAlign: 'center', mt: 1,
                '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                animation: 'fadeIn 0.3s ease-out 0.2s both'
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { setNaraSugerencias(null); setShowNaraSugerencias(false); onClose(); }}
                  sx={{
                    ...monoStyle,
                    color: '#64748b',
                    borderColor: 'rgba(100, 116, 139, 0.3)',
                    fontSize: 11,
                    borderRadius: 2,
                    px: 3,
                    '&:hover': { borderColor: '#10b981', color: '#10b981' }
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
    <ConsultaVozStreaming
      open={vozStreamingOpen}
      onClose={() => setVozStreamingOpen(false)}
      consultaId={consultaId}
    />
    </>
  );
};

// ============================================================================
// Campo de Frecuencia — TextField + lista inline (mismo patrón que Buscar Medicamento)
// ============================================================================
function CampoFrecuencia({ valor, onChange, inputElegante }) {
  const [foco, setFoco] = useState(false);
  const cerrarTimer = useRef(null);
  const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = norm(valor || '');

  // Filtrar opciones por lo que escribió el usuario
  const opcionesFiltradas = q
    ? FRECUENCIAS_MEDICAMENTOS.filter(o => norm(o.label).includes(q))
    : FRECUENCIAS_MEDICAMENTOS;

  // Agrupar por categoría
  const grupos = {};
  for (const op of opcionesFiltradas) {
    if (!grupos[op.categoria]) grupos[op.categoria] = [];
    grupos[op.categoria].push(op);
  }

  // Mostrar lista cuando: tiene foco AND hay opciones AND el valor exacto no coincide con una sola opción
  const valorEsExacto = FRECUENCIAS_MEDICAMENTOS.some(o => norm(o.label) === q);
  const mostrarLista = foco && opcionesFiltradas.length > 0 && !valorEsExacto;

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth size="small"
        label="Frecuencia"
        placeholder="Cada 8 horas · Antes de las comidas · Una vez a la semana..."
        value={valor || ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          if (cerrarTimer.current) clearTimeout(cerrarTimer.current);
          setFoco(true);
        }}
        onBlur={() => {
          // Delay para permitir click en una opción antes de cerrar
          cerrarTimer.current = setTimeout(() => setFoco(false), 180);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <ScheduleIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
            </InputAdornment>
          ),
        }}
        sx={inputElegante}
      />

      {/* Lista inline de opciones */}
      {mostrarLista && (
        <Box sx={{
          mt: 0.7,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          background: '#fff',
          maxHeight: 280,
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          // No es absoluto — es parte del flujo, igual que en Buscar Medicamento
        }}>
          {Object.entries(grupos).map(([categoria, opciones]) => (
            <Box key={categoria}>
              <Typography sx={{
                px: 1.5, py: 0.6,
                fontSize: 9.5, fontWeight: 800,
                color: '#6366f1',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                background: '#eef2ff',
                borderBottom: '1px solid #c7d2fe',
              }}>
                {categoria}
              </Typography>
              {opciones.map((op) => (
                <Box
                  key={op.label}
                  onMouseDown={(e) => {
                    e.preventDefault(); // evita que blur se dispare antes del click
                    onChange(op.label);
                    setFoco(false);
                  }}
                  sx={{
                    px: 1.5, py: 0.8,
                    cursor: 'pointer',
                    fontSize: 12.5,
                    color: '#1e293b',
                    borderBottom: '1px solid #f1f5f9',
                    '&:hover': { background: '#f8fafc', color: '#4338ca' },
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  {op.label}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Asistente IA — Impresión Diagnóstica (Fase 1)
// ============================================================================
function AsistenteDiagnosticoIA({ contexto, onAceptar, userId, diagnosticosActuales = [] }) {
  const [cargando, setCargando] = useState(false);
  const [data, setData] = useState(null);  // { sugerencias, advertencias, resumen, disclaimer }
  const [error, setError] = useState('');
  const [colapsado, setColapsado] = useState(false);

  const consultarIA = async () => {
    setError('');
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/emr/ia/sugerir-diagnosticos-consulta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(contexto),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.substring(0, 200)}`);
      }
      const json = await res.json();
      setData({
        sugerencias: json.sugerencias || [],
        advertencias: json.advertencias || [],
        resumen: json.resumen_clinico || '',
        disclaimer: json.disclaimer || '',
      });
      setColapsado(false);
    } catch (e) {
      console.error('[IA-DIAGNOSTICO]', e);
      setError(e.message || 'No se pudo consultar al asistente IA');
    } finally {
      setCargando(false);
    }
  };

  // Tiene info mínima para consultar?
  const hayContextoSuficiente = Boolean(
    contexto.motivo_consulta || contexto.padecimiento_actual ||
    contexto.signos_vitales?.tension_arterial || contexto.exploracion_fisica
  );

  const colorConfianza = (c) => {
    if (c === 'alta')  return { color: '#15803d', bg: '#dcfce7', borde: '#86efac', label: '⭐ Alta' };
    if (c === 'media') return { color: '#a16207', bg: '#fef3c7', borde: '#fde68a', label: '⚠ Media' };
    return { color: '#475569', bg: '#f1f5f9', borde: '#cbd5e1', label: '· Baja' };
  };

  return (
    <Box sx={{
      borderRadius: 2.5,
      border: '1px solid #c7d2fe',
      background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
      mb: 2,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        p: 1.5,
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
        borderBottom: data ? '1px solid #c7d2fe' : 'none',
      }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
        }}>
          <PsychologyIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1e1b4b' }}>
            Asistente diagnóstico IA
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
            Sugiere códigos CIE-10 con base en la información clínica · Apoyo, no reemplaza tu criterio
          </Typography>
        </Box>
        {data && (
          <IconButton size="small" onClick={() => setColapsado(!colapsado)}>
            {colapsado ? <NextIcon sx={{ fontSize: 18 }} /> : <PrevIcon sx={{ fontSize: 18, transform: 'rotate(-90deg)' }} />}
          </IconButton>
        )}
        <Button
          size="small"
          variant="contained"
          disabled={cargando || !hayContextoSuficiente}
          onClick={consultarIA}
          startIcon={cargando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PsychologyIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12,
            borderRadius: 2, px: 1.8,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {cargando ? 'Analizando...' : data ? 'Volver a sugerir' : 'Solicitar sugerencias'}
        </Button>
      </Box>

      {!hayContextoSuficiente && !data && (
        <Box sx={{ px: 1.5, py: 1, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
          💡 Completá motivo, padecimiento o exploración primero para tener mejores sugerencias.
        </Box>
      )}

      {error && (
        <Box sx={{ p: 1.5, background: '#fef2f2', borderTop: '1px solid #fecaca' }}>
          <Typography sx={{ fontSize: 11.5, color: '#b91c1c' }}>⚠ {error}</Typography>
        </Box>
      )}

      {data && !colapsado && (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Disclaimer SIEMPRE arriba — visualmente prominente */}
          {data.disclaimer && (
            <Box sx={{
              p: 1.2, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
              border: '1px solid #fde68a',
              borderLeft: '4px solid #dc2626',
              display: 'flex', alignItems: 'flex-start', gap: 0.8,
            }}>
              <Box sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0, mt: 0.1 }}>⚠️</Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: '#991b1b',
                  textTransform: 'uppercase', letterSpacing: 0.7, mb: 0.3,
                }}>
                  Aviso importante
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#78350f', lineHeight: 1.5, fontWeight: 500 }}>
                  {data.disclaimer}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Resumen clínico */}
          {data.resumen && (
            <Box sx={{
              p: 1, borderRadius: 1.5,
              background: '#fff', border: '1px solid #e2e8f0',
              borderLeft: '3px solid #6366f1',
            }}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
                Resumen clínico de la IA
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#1e293b', lineHeight: 1.5 }}>
                {data.resumen}
              </Typography>
            </Box>
          )}

          {/* Advertencias */}
          {data.advertencias && data.advertencias.length > 0 && (
            <Box sx={{
              p: 1, borderRadius: 1.5,
              background: '#fef2f2', border: '1px solid #fecaca',
              borderLeft: '3px solid #dc2626',
            }}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#991b1b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
                Advertencias / Banderas rojas
              </Typography>
              {data.advertencias.map((adv, i) => (
                <Typography key={i} sx={{ fontSize: 11.5, color: '#7f1d1d', lineHeight: 1.4 }}>
                  • {adv}
                </Typography>
              ))}
            </Box>
          )}

          {/* Sugerencias diagnósticas */}
          {data.sugerencias.length === 0 ? (
            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic', textAlign: 'center', py: 1 }}>
              La IA no encontró sugerencias claras. Considerá completar más información clínica.
            </Typography>
          ) : (
            <>
              {/* Hint sobre cómo funciona la asignación principal/secundario */}
              <Box sx={{
                p: 0.9, borderRadius: 1.5,
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                display: 'flex', alignItems: 'flex-start', gap: 0.6,
              }}>
                <Box sx={{ fontSize: 13, lineHeight: 1, mt: 0.1 }}>ℹ️</Box>
                <Typography sx={{ fontSize: 10.5, color: '#1e3a8a', lineHeight: 1.5 }}>
                  <b>Cómo funciona:</b> al agregar una sugerencia, si <b>aún no hay diagnóstico principal</b>, se asigna como <b style={{ color: '#15803d' }}>principal</b>. Si ya existe uno, los siguientes se agregan como <b>secundarios</b>. Los duplicados (mismo código CIE-10) son ignorados con aviso.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                {data.sugerencias.map((sug, i) => {
                  const conf = colorConfianza(sug.confianza);
                  // Calcular en VIVO qué pasaría si el doctor hace click
                  const yaAgregado = diagnosticosActuales.some(d => d.codigo_cie10 === sug.codigo_cie10);
                  const yaHayPrincipal = diagnosticosActuales.some(d => d.tipo_diagnostico === 'principal');
                  const tipoQueSeAsigna = (sug.tipo_sugerido === 'principal' && !yaHayPrincipal)
                    ? 'principal' : 'secundario';
                  const labelBoton = yaAgregado
                    ? '✓ Ya agregado'
                    : tipoQueSeAsigna === 'principal'
                      ? 'Agregar como principal'
                      : 'Agregar como secundario';
                  const colorBoton = yaAgregado ? '#94a3b8' : conf.color;
                  const bgHoverBoton = yaAgregado ? '#f1f5f9' : conf.bg;

                  return (
                    <Box key={i} sx={{
                      p: 1, borderRadius: 1.5,
                      background: yaAgregado ? '#f8fafc' : '#fff',
                      border: `1px solid ${conf.borde}`,
                      borderLeft: `4px solid ${conf.color}`,
                      opacity: yaAgregado ? 0.7 : 1,
                      display: 'flex', alignItems: 'flex-start', gap: 1,
                      transition: 'all 0.15s',
                      '&:hover': { boxShadow: yaAgregado ? 'none' : `0 2px 10px ${conf.color}22` },
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.3 }}>
                          <Box sx={{
                            px: 0.6, py: 0.05, borderRadius: 0.7,
                            background: conf.bg, color: conf.color,
                            fontSize: 9, fontWeight: 800, letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}>
                            {conf.label}
                          </Box>
                          {sug.tipo_sugerido === 'principal' && (
                            <Box sx={{
                              px: 0.6, py: 0.05, borderRadius: 0.7,
                              background: '#dcfce7', color: '#15803d',
                              fontSize: 9, fontWeight: 800, letterSpacing: 0.3,
                            }}>
                              SUGERIDO PRINCIPAL
                            </Box>
                          )}
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1e293b' }}>
                            {sug.codigo_cie10}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: '#1e293b' }}>
                            · {sug.descripcion}
                          </Typography>
                        </Box>
                        {sug.justificacion && (
                          <Typography sx={{ fontSize: 11, color: '#475569', lineHeight: 1.4, fontStyle: 'italic' }}>
                            {sug.justificacion}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant={yaAgregado ? 'text' : 'outlined'}
                        disabled={yaAgregado}
                        startIcon={yaAgregado ? null : <AddIcon sx={{ fontSize: 14 }} />}
                        onClick={() => onAceptar(sug)}
                        sx={{
                          textTransform: 'none', fontWeight: 700, fontSize: 11,
                          borderRadius: 1.5, px: 1.2, flexShrink: 0,
                          minWidth: 165,
                          borderColor: colorBoton, color: colorBoton,
                          '&:hover': { background: bgHoverBoton, borderColor: colorBoton },
                          '&.Mui-disabled': { color: '#94a3b8' },
                        }}
                      >
                        {labelBoton}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Asistente IA — Estudios de Laboratorio e Interconsultas (Fase 2)
// ============================================================================
function AsistenteEstudiosIA({ contexto, ordenes = [], onOrdenesChange, consultaId, userId, API_URL: apiUrl, onSnackbar }) {
  const [cargando, setCargando] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [seleccionEstudios, setSeleccionEstudios] = useState(new Set());
  const [colapsado, setColapsado] = useState(false);
  const [creandoLab, setCreandoLab] = useState(false);
  const [creandoIC, setCreandoIC] = useState(null);  // índice de la interconsulta que se está creando

  const consultarIA = async () => {
    setError('');
    setCargando(true);
    try {
      const res = await fetch(`${apiUrl}/emr/ia/sugerir-estudios-interconsultas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(contexto),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.substring(0, 200)}`);
      }
      const json = await res.json();
      setData(json);
      setSeleccionEstudios(new Set());
      setColapsado(false);
    } catch (e) {
      console.error('[IA-ESTUDIOS]', e);
      setError(e.message || 'No se pudo consultar al asistente IA');
    } finally {
      setCargando(false);
    }
  };

  const hayContextoSuficiente = Boolean(
    contexto.motivo_consulta || contexto.padecimiento_actual ||
    (contexto.diagnosticos && contexto.diagnosticos.length > 0) ||
    contexto.impresion_diagnostica_actual
  );

  // Estudios ya agregados (en cualquier orden de laboratorio existente)
  const estudiosYaAgregados = new Set();
  ordenes.filter(o => o.tipo === 'laboratorio').forEach(o => {
    (o.contenido?.estudios || []).forEach(e => {
      estudiosYaAgregados.add((e.nombre || '').toLowerCase().trim());
    });
  });

  // Interconsultas ya creadas (por especialidad)
  const interconsultasYaAgregadas = new Set();
  ordenes.filter(o => o.tipo === 'interconsulta').forEach(o => {
    interconsultasYaAgregadas.add((o.contenido?.especialidad || '').toLowerCase().trim());
  });

  const toggleEstudio = (idx) => {
    const nuevo = new Set(seleccionEstudios);
    if (nuevo.has(idx)) nuevo.delete(idx); else nuevo.add(idx);
    setSeleccionEstudios(nuevo);
  };

  // Crear UNA orden de laboratorio con los estudios seleccionados
  const crearOrdenLaboratorio = async () => {
    if (!consultaId) {
      onSnackbar('Guardá primero la consulta como borrador para poder crear órdenes', 'warning');
      return;
    }
    if (seleccionEstudios.size === 0) return;
    const estudiosElegidos = [...seleccionEstudios]
      .map(idx => data.estudios[idx])
      .filter(Boolean)
      .filter(e => !estudiosYaAgregados.has((e.nombre || '').toLowerCase().trim()));

    if (estudiosElegidos.length === 0) {
      onSnackbar('Todos los estudios seleccionados ya están en una orden previa', 'info');
      return;
    }

    setCreandoLab(true);
    try {
      const contenido = {
        estudios: estudiosElegidos.map(e => ({
          nombre: e.nombre,
          codigo: e.codigo || '',
        })),
      };
      const indicaciones = estudiosElegidos.map(e => e.razon).filter(Boolean).join(' · ');
      const res = await fetch(`${apiUrl}/emr/consultas/${consultaId}/ordenes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ tipo: 'laboratorio', contenido, indicaciones: indicaciones || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const nueva = await res.json();
      onOrdenesChange([...ordenes, nueva]);
      setSeleccionEstudios(new Set());
      onSnackbar(`✅ Orden de laboratorio creada con ${estudiosElegidos.length} estudio${estudiosElegidos.length !== 1 ? 's' : ''}`, 'success');
    } catch (e) {
      console.error('[IA-LAB]', e);
      onSnackbar('❌ No se pudo crear la orden: ' + (e.message || ''), 'error');
    } finally {
      setCreandoLab(false);
    }
  };

  // Crear UNA interconsulta individual
  const crearInterconsulta = async (idx) => {
    if (!consultaId) {
      onSnackbar('Guardá primero la consulta como borrador para poder crear órdenes', 'warning');
      return;
    }
    const ic = data.interconsultas[idx];
    if (!ic) return;

    if (interconsultasYaAgregadas.has((ic.especialidad || '').toLowerCase().trim())) {
      onSnackbar(`Ya hay una interconsulta a ${ic.especialidad}`, 'info');
      return;
    }

    setCreandoIC(idx);
    try {
      const contenido = {
        especialidad: ic.especialidad,
        motivo: ic.motivo,
      };
      const res = await fetch(`${apiUrl}/emr/consultas/${consultaId}/ordenes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          tipo: 'interconsulta',
          contenido,
          indicaciones: ic.caracter === 'urgente' ? 'URGENTE' : null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const nueva = await res.json();
      onOrdenesChange([...ordenes, nueva]);
      onSnackbar(`✅ Interconsulta a ${ic.especialidad} creada`, 'success');
    } catch (e) {
      console.error('[IA-IC]', e);
      onSnackbar('❌ No se pudo crear la interconsulta: ' + (e.message || ''), 'error');
    } finally {
      setCreandoIC(null);
    }
  };

  const colorPrioridad = (p) => {
    if (p === 'alta')  return { color: '#15803d', bg: '#dcfce7', borde: '#86efac', label: 'ALTA' };
    if (p === 'media') return { color: '#a16207', bg: '#fef3c7', borde: '#fde68a', label: 'MEDIA' };
    return { color: '#475569', bg: '#f1f5f9', borde: '#cbd5e1', label: 'BAJA' };
  };

  return (
    <Box sx={{
      borderRadius: 2.5,
      border: '1px solid #67e8f9',
      background: 'linear-gradient(135deg, #ecfeff 0%, #eef2ff 100%)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
        background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.06) 0%, rgba(99, 102, 241, 0.06) 100%)',
        borderBottom: data ? '1px solid #a5f3fc' : 'none',
      }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(8, 145, 178, 0.35)',
        }}>
          <BiotechIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0c4a6e' }}>
            Asistente IA · Estudios e Interconsultas
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
            Sugiere estudios de laboratorio e interconsultas con base en el cuadro clínico · Apoyo, no reemplaza tu criterio
          </Typography>
        </Box>
        {data && (
          <IconButton size="small" onClick={() => setColapsado(!colapsado)}>
            {colapsado ? <NextIcon sx={{ fontSize: 18 }} /> : <PrevIcon sx={{ fontSize: 18, transform: 'rotate(-90deg)' }} />}
          </IconButton>
        )}
        <Button
          size="small"
          variant="contained"
          disabled={cargando || !hayContextoSuficiente}
          onClick={consultarIA}
          startIcon={cargando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <BiotechIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12,
            borderRadius: 2, px: 1.8,
            background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 100%)',
            boxShadow: '0 4px 10px rgba(8, 145, 178, 0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #0e7490 0%, #4f46e5 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {cargando ? 'Analizando...' : data ? 'Volver a sugerir' : 'Solicitar sugerencias'}
        </Button>
      </Box>

      {!hayContextoSuficiente && !data && (
        <Box sx={{ px: 1.5, py: 1, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
          💡 Completá motivo, padecimiento o agregá diagnósticos primero para tener mejores sugerencias.
        </Box>
      )}

      {error && (
        <Box sx={{ p: 1.5, background: '#fef2f2', borderTop: '1px solid #fecaca' }}>
          <Typography sx={{ fontSize: 11.5, color: '#b91c1c' }}>⚠ {error}</Typography>
        </Box>
      )}

      {data && !colapsado && (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {/* Disclaimer SIEMPRE arriba — visualmente prominente */}
          {data.disclaimer && (
            <Box sx={{
              p: 1.2, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
              border: '1px solid #fde68a',
              borderLeft: '4px solid #dc2626',
              display: 'flex', alignItems: 'flex-start', gap: 0.8,
            }}>
              <Box sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0, mt: 0.1 }}>⚠️</Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: '#991b1b',
                  textTransform: 'uppercase', letterSpacing: 0.7, mb: 0.3,
                }}>
                  Aviso importante
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#78350f', lineHeight: 1.5, fontWeight: 500 }}>
                  {data.disclaimer}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Hint */}
          <Box sx={{
            p: 0.9, borderRadius: 1.5,
            background: '#eff6ff', border: '1px solid #bfdbfe',
            display: 'flex', alignItems: 'flex-start', gap: 0.6,
          }}>
            <Box sx={{ fontSize: 13, lineHeight: 1, mt: 0.1 }}>ℹ️</Box>
            <Typography sx={{ fontSize: 10.5, color: '#1e3a8a', lineHeight: 1.5 }}>
              <b>Cómo funciona:</b> Marcá los estudios de laboratorio que quieras incluir y presioná <b>"Crear orden de laboratorio"</b> — todos van en una sola orden. Las interconsultas se crean por separado, una por especialidad. Los duplicados no se agregan.
            </Typography>
          </Box>

          {/* Advertencias */}
          {data.advertencias && data.advertencias.length > 0 && (
            <Box sx={{
              p: 1, borderRadius: 1.5,
              background: '#fef2f2', border: '1px solid #fecaca',
              borderLeft: '3px solid #dc2626',
            }}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#991b1b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
                Advertencias
              </Typography>
              {data.advertencias.map((adv, i) => (
                <Typography key={i} sx={{ fontSize: 11.5, color: '#7f1d1d', lineHeight: 1.4 }}>
                  • {adv}
                </Typography>
              ))}
            </Box>
          )}

          {/* === ESTUDIOS DE LABORATORIO === */}
          {Array.isArray(data.estudios) && data.estudios.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.7 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 800, color: '#0c4a6e',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  🧪 Estudios sugeridos ({data.estudios.length})
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={seleccionEstudios.size === 0 || creandoLab || !consultaId}
                  onClick={crearOrdenLaboratorio}
                  startIcon={creandoLab ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : <AddIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: 11,
                    borderRadius: 1.5, px: 1.3, py: 0.4,
                    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #0e7490 0%, #0891b2 100%)' },
                    '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
                  }}
                >
                  {creandoLab ? 'Creando...' : `Crear orden${seleccionEstudios.size > 0 ? ` con ${seleccionEstudios.size} estudio${seleccionEstudios.size !== 1 ? 's' : ''}` : ''}`}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {data.estudios.map((est, i) => {
                  const yaAgregado = estudiosYaAgregados.has((est.nombre || '').toLowerCase().trim());
                  const seleccionado = seleccionEstudios.has(i);
                  const prio = colorPrioridad(est.prioridad);
                  return (
                    <Box
                      key={i}
                      onClick={() => !yaAgregado && toggleEstudio(i)}
                      sx={{
                        p: 0.9, borderRadius: 1.5,
                        cursor: yaAgregado ? 'default' : 'pointer',
                        background: yaAgregado ? '#f1f5f9' : seleccionado ? '#ecfeff' : '#fff',
                        border: `1px solid ${seleccionado ? '#67e8f9' : prio.borde}`,
                        borderLeft: `3px solid ${yaAgregado ? '#94a3b8' : prio.color}`,
                        opacity: yaAgregado ? 0.7 : 1,
                        display: 'flex', alignItems: 'flex-start', gap: 0.8,
                        transition: 'all 0.1s',
                        '&:hover': yaAgregado ? {} : { background: seleccionado ? '#cffafe' : '#f8fafc' },
                      }}
                    >
                      <Box sx={{
                        width: 18, height: 18, borderRadius: 0.5, flexShrink: 0, mt: 0.2,
                        background: yaAgregado ? '#10b981' : seleccionado ? '#0891b2' : '#fff',
                        border: `1.5px solid ${yaAgregado ? '#10b981' : seleccionado ? '#0891b2' : '#cbd5e1'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 12, fontWeight: 800,
                      }}>
                        {(yaAgregado || seleccionado) ? '✓' : ''}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.2 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
                            {est.nombre}
                          </Typography>
                          {est.tipo && est.tipo !== 'laboratorio' && (
                            <Chip
                              label={est.tipo} size="small"
                              sx={{ height: 16, fontSize: 9, background: '#e0e7ff', color: '#4338ca', fontWeight: 700 }}
                            />
                          )}
                          <Box sx={{
                            ml: 'auto',
                            px: 0.5, py: 0.05, borderRadius: 0.6,
                            background: prio.bg, color: prio.color,
                            fontSize: 8.5, fontWeight: 800, letterSpacing: 0.3,
                          }}>
                            {prio.label}
                          </Box>
                          {yaAgregado && (
                            <Box sx={{
                              px: 0.5, py: 0.05, borderRadius: 0.6,
                              background: '#dcfce7', color: '#15803d',
                              fontSize: 9, fontWeight: 800,
                            }}>
                              YA AGREGADO
                            </Box>
                          )}
                        </Box>
                        {est.razon && (
                          <Typography sx={{ fontSize: 11, color: '#475569', fontStyle: 'italic', lineHeight: 1.4 }}>
                            {est.razon}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* === INTERCONSULTAS === */}
          {Array.isArray(data.interconsultas) && data.interconsultas.length > 0 && (
            <Box>
              <Typography sx={{
                fontSize: 11, fontWeight: 800, color: '#0c4a6e',
                textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.7,
              }}>
                🏥 Interconsultas sugeridas ({data.interconsultas.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {data.interconsultas.map((ic, i) => {
                  const yaAgregada = interconsultasYaAgregadas.has((ic.especialidad || '').toLowerCase().trim());
                  const prio = colorPrioridad(ic.prioridad);
                  const esUrgente = ic.caracter === 'urgente';
                  return (
                    <Box key={i} sx={{
                      p: 0.9, borderRadius: 1.5,
                      background: yaAgregada ? '#f1f5f9' : '#fff',
                      border: `1px solid ${esUrgente ? '#fecaca' : prio.borde}`,
                      borderLeft: `3px solid ${yaAgregada ? '#94a3b8' : esUrgente ? '#dc2626' : prio.color}`,
                      opacity: yaAgregada ? 0.7 : 1,
                      display: 'flex', alignItems: 'flex-start', gap: 1,
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.2 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>
                            {ic.especialidad}
                          </Typography>
                          {esUrgente && (
                            <Box sx={{
                              px: 0.5, py: 0.05, borderRadius: 0.6,
                              background: '#fef2f2', color: '#dc2626',
                              fontSize: 9, fontWeight: 800,
                            }}>
                              URGENTE
                            </Box>
                          )}
                          <Box sx={{
                            ml: 'auto',
                            px: 0.5, py: 0.05, borderRadius: 0.6,
                            background: prio.bg, color: prio.color,
                            fontSize: 8.5, fontWeight: 800, letterSpacing: 0.3,
                          }}>
                            {prio.label}
                          </Box>
                          {yaAgregada && (
                            <Box sx={{
                              px: 0.5, py: 0.05, borderRadius: 0.6,
                              background: '#dcfce7', color: '#15803d',
                              fontSize: 9, fontWeight: 800,
                            }}>
                              YA AGREGADA
                            </Box>
                          )}
                        </Box>
                        {ic.motivo && (
                          <Typography sx={{ fontSize: 11, color: '#475569', fontStyle: 'italic', lineHeight: 1.4 }}>
                            {ic.motivo}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={yaAgregada || creandoIC === i || !consultaId}
                        onClick={() => crearInterconsulta(i)}
                        startIcon={creandoIC === i ? <CircularProgress size={12} /> : <AddIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          textTransform: 'none', fontWeight: 700, fontSize: 10.5,
                          borderRadius: 1.5, px: 1.2, flexShrink: 0,
                          minWidth: 110,
                          borderColor: yaAgregada ? '#cbd5e1' : '#0891b2',
                          color: yaAgregada ? '#94a3b8' : '#0891b2',
                          '&:hover': yaAgregada ? {} : { background: '#ecfeff', borderColor: '#0891b2' },
                        }}
                      >
                        {yaAgregada ? 'Ya agregada' : creandoIC === i ? 'Creando...' : 'Crear'}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {(!data.estudios?.length && !data.interconsultas?.length) && (
            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic', textAlign: 'center', py: 1 }}>
              La IA no encontró sugerencias claras de estudios o interconsultas para este caso.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Asistente IA — Plan de Tratamiento (Fase 3)
// ============================================================================
function AsistenteTratamientoIA({
  contexto, userId,
  planTratamientoActual = '', onLlenarPlanTratamiento,
  pronosticoActual = '', onEstablecerPronostico,
  indicacionesActuales = '', onUsarIndicaciones,
  onSnackbar,
}) {
  const [cargando, setCargando] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [colapsado, setColapsado] = useState(false);

  const consultarIA = async () => {
    setError('');
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/emr/ia/sugerir-tratamiento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(contexto),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.substring(0, 200)}`);
      }
      const json = await res.json();
      setData(json);
      setColapsado(false);
    } catch (e) {
      console.error('[IA-TRATAMIENTO]', e);
      setError(e.message || 'No se pudo consultar al asistente IA');
    } finally {
      setCargando(false);
    }
  };

  const hayContextoSuficiente = Boolean(
    (contexto.diagnosticos && contexto.diagnosticos.length > 0) ||
    contexto.impresion_diagnostica_actual
  );

  const colorPrioridad = (p) => {
    if (p === 'alta')  return { color: '#15803d', bg: '#dcfce7', borde: '#86efac', label: 'ALTA' };
    if (p === 'media') return { color: '#a16207', bg: '#fef3c7', borde: '#fde68a', label: 'MEDIA' };
    return { color: '#475569', bg: '#f1f5f9', borde: '#cbd5e1', label: 'BAJA' };
  };

  const aplicarIndicaciones = () => {
    if (!data.indicaciones_generales) return;
    if (indicacionesActuales && indicacionesActuales.trim().length > 0) {
      // Hay contenido existente — append con separador
      onUsarIndicaciones(`${indicacionesActuales}\n\n${data.indicaciones_generales}`);
      onSnackbar('✅ Indicaciones agregadas (al final del texto existente)', 'success');
    } else {
      onUsarIndicaciones(data.indicaciones_generales);
      onSnackbar('✅ Indicaciones cargadas en el plan', 'success');
    }
  };

  return (
    <Box sx={{
      borderRadius: 2.5,
      border: '1px solid #fcd34d',
      background: 'linear-gradient(135deg, #fffbeb 0%, #fef9c3 100%)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(99, 102, 241, 0.06) 100%)',
        borderBottom: data ? '1px solid #fcd34d' : 'none',
      }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'linear-gradient(135deg, #f59e0b 0%, #6366f1 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
        }}>
          <MedicationIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#78350f' }}>
            Asistente IA · Plan de Tratamiento
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
            Sugiere medicamentos, indicaciones y advierte sobre alergias · Apoyo, no reemplaza tu criterio
          </Typography>
        </Box>
        {data && (
          <IconButton size="small" onClick={() => setColapsado(!colapsado)}>
            {colapsado ? <NextIcon sx={{ fontSize: 18 }} /> : <PrevIcon sx={{ fontSize: 18, transform: 'rotate(-90deg)' }} />}
          </IconButton>
        )}
        <Button
          size="small"
          variant="contained"
          disabled={cargando || !hayContextoSuficiente}
          onClick={consultarIA}
          startIcon={cargando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <MedicationIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12,
            borderRadius: 2, px: 1.8,
            background: 'linear-gradient(135deg, #f59e0b 0%, #6366f1 100%)',
            boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #4f46e5 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {cargando ? 'Analizando...' : data ? 'Volver a sugerir' : 'Solicitar sugerencias'}
        </Button>
      </Box>

      {!hayContextoSuficiente && !data && (
        <Box sx={{ px: 1.5, py: 1, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
          💡 Agregá al menos un diagnóstico CIE-10 o impresión diagnóstica para recibir sugerencias terapéuticas.
        </Box>
      )}

      {error && (
        <Box sx={{ p: 1.5, background: '#fef2f2', borderTop: '1px solid #fecaca' }}>
          <Typography sx={{ fontSize: 11.5, color: '#b91c1c' }}>⚠ {error}</Typography>
        </Box>
      )}

      {data && !colapsado && (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {/* Disclaimer SIEMPRE arriba — visualmente prominente */}
          {data.disclaimer && (
            <Box sx={{
              p: 1.2, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
              border: '1px solid #fde68a',
              borderLeft: '4px solid #dc2626',
              display: 'flex', alignItems: 'flex-start', gap: 0.8,
            }}>
              <Box sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0, mt: 0.1 }}>⚠️</Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: '#991b1b',
                  textTransform: 'uppercase', letterSpacing: 0.7, mb: 0.3,
                }}>
                  Aviso importante
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#78350f', lineHeight: 1.5, fontWeight: 500 }}>
                  {data.disclaimer}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Advertencias críticas (alergias, interacciones, ajustes) */}
          {data.advertencias && data.advertencias.length > 0 && (
            <Box sx={{
              p: 1.2, borderRadius: 1.5,
              background: '#fef2f2', border: '1px solid #fecaca',
              borderLeft: '4px solid #dc2626',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Box sx={{ fontSize: 14, lineHeight: 1 }}>🚨</Box>
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: '#991b1b',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  Advertencias críticas ({data.advertencias.length})
                </Typography>
              </Box>
              {data.advertencias.map((adv, i) => (
                <Typography key={i} sx={{ fontSize: 11.5, color: '#7f1d1d', lineHeight: 1.5, mb: 0.3 }}>
                  • {adv}
                </Typography>
              ))}
            </Box>
          )}

          {/* Resumen del enfoque terapéutico */}
          {data.resumen && (
            <Box sx={{
              p: 1, borderRadius: 1.5,
              background: '#fff', border: '1px solid #e2e8f0',
              borderLeft: '3px solid #6366f1',
            }}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
                Enfoque terapéutico sugerido
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#1e293b', lineHeight: 1.5 }}>
                {data.resumen}
              </Typography>
            </Box>
          )}

          {/* Hint informativo */}
          <Box sx={{
            p: 0.9, borderRadius: 1.5,
            background: '#eff6ff', border: '1px solid #bfdbfe',
            display: 'flex', alignItems: 'flex-start', gap: 0.6,
          }}>
            <Box sx={{ fontSize: 13, lineHeight: 1, mt: 0.1 }}>ℹ️</Box>
            <Typography sx={{ fontSize: 10.5, color: '#1e3a8a', lineHeight: 1.5 }}>
              <b>Cómo funciona:</b> los medicamentos sugeridos son <b>referencia educativa</b> con nombres genéricos — el médico decide qué prescribe en la pestaña <b>Receta</b>. Los botones <b>"→ Llenar..."</b> rellenan los campos de la pestaña actual. Los textos existentes no se sobrescriben (se agregan al final con separador).
            </Typography>
          </Box>

          {/* PLAN DE TRATAMIENTO SUGERIDO */}
          {data.plan_tratamiento_texto && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.7, gap: 1 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 800, color: '#312e81',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  📋 Indicaciones terapéuticas sugeridas
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    const nuevo = planTratamientoActual?.trim()
                      ? `${planTratamientoActual}\n\n${data.plan_tratamiento_texto}`
                      : data.plan_tratamiento_texto;
                    onLlenarPlanTratamiento(nuevo);
                    onSnackbar(
                      planTratamientoActual?.trim()
                        ? '✅ Indicaciones terapéuticas agregadas al final'
                        : '✅ Indicaciones terapéuticas cargadas en esta pestaña',
                      'success'
                    );
                  }}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: 11,
                    borderRadius: 1.5, px: 1.3, py: 0.4, flexShrink: 0,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
                  }}
                >
                  {planTratamientoActual?.trim() ? 'Agregar al final' : 'Llenar Plan de Tratamiento'}
                </Button>
              </Box>
              <Box sx={{
                p: 1, borderRadius: 1.5,
                background: '#fff', border: '1px solid #c7d2fe',
                borderLeft: '3px solid #6366f1',
              }}>
                <Typography sx={{ fontSize: 12, color: '#0f172a', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {data.plan_tratamiento_texto}
                </Typography>
              </Box>
            </Box>
          )}

          {/* PRONÓSTICO SUGERIDO */}
          {data.pronostico_sugerido && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.7, gap: 1 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 800, color: '#15803d',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  🎯 Pronóstico sugerido
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={pronosticoActual === data.pronostico_sugerido}
                  onClick={() => {
                    onEstablecerPronostico(data.pronostico_sugerido);
                    onSnackbar(
                      `✅ Pronóstico establecido como "${data.pronostico_sugerido.charAt(0).toUpperCase() + data.pronostico_sugerido.slice(1)}"`,
                      'success'
                    );
                  }}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: 11,
                    borderRadius: 1.5, px: 1.3, py: 0.4, flexShrink: 0,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                    '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
                  }}
                >
                  {pronosticoActual === data.pronostico_sugerido
                    ? '✓ Ya establecido'
                    : 'Establecer pronóstico'}
                </Button>
              </Box>
              <Box sx={{
                p: 1, borderRadius: 1.5,
                background: '#fff', border: '1px solid #86efac',
                borderLeft: '3px solid #10b981',
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <Box sx={{
                  px: 1, py: 0.3, borderRadius: 1,
                  background: data.pronostico_sugerido === 'bueno' ? '#dcfce7'
                    : data.pronostico_sugerido === 'reservado' ? '#fef3c7' : '#fef2f2',
                  color: data.pronostico_sugerido === 'bueno' ? '#15803d'
                    : data.pronostico_sugerido === 'reservado' ? '#a16207' : '#991b1b',
                  fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  {data.pronostico_sugerido}
                </Box>
                {data.pronostico_justificacion && (
                  <Typography sx={{ fontSize: 11.5, color: '#475569', fontStyle: 'italic', flex: 1 }}>
                    {data.pronostico_justificacion}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* ESQUEMA FARMACOLÓGICO DE REFERENCIA — solo lectura, sin botones */}
          {Array.isArray(data.medicamentos) && data.medicamentos.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.7 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 800, color: '#78350f',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  💊 Esquema farmacológico de referencia ({data.medicamentos.length})
                </Typography>
                <Box sx={{
                  ml: 0.5, px: 0.5, py: 0.05, borderRadius: 0.6,
                  background: '#fef3c7', color: '#a16207',
                  fontSize: 9, fontWeight: 800, letterSpacing: 0.3,
                }}>
                  REFERENCIA · NO PRESCRIBE
                </Box>
              </Box>
              <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic', mb: 0.7 }}>
                Nombres genéricos. La prescripción se realiza manualmente en la pestaña <b>Receta</b>.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                {data.medicamentos.map((med, i) => {
                  const prio = colorPrioridad(med.prioridad);
                  return (
                    <Box key={i} sx={{
                      p: 1, borderRadius: 1.5,
                      background: '#fff',
                      border: `1px solid ${prio.borde}`,
                      borderLeft: `4px solid ${prio.color}`,
                    }}>
                      {/* Línea 1 — nombre genérico + presentación + dosis */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.3 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                          {med.nombre}
                        </Typography>
                        {med.presentacion && (
                          <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                            · {med.presentacion}
                          </Typography>
                        )}
                        {med.dosis && (
                          <Typography sx={{ fontSize: 12, color: '#a16207', fontWeight: 800 }}>
                            {med.dosis}
                          </Typography>
                        )}
                        <Box sx={{
                          ml: 'auto',
                          px: 0.5, py: 0.05, borderRadius: 0.6,
                          background: prio.bg, color: prio.color,
                          fontSize: 8.5, fontWeight: 800, letterSpacing: 0.3,
                        }}>
                          {prio.label}
                        </Box>
                      </Box>
                      {/* Línea 2 — chips con vía / frecuencia / duración */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.4 }}>
                        {med.via && (
                          <Chip size="small" label={`Vía ${med.via}`}
                            sx={{ height: 19, fontSize: 10, background: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }} />
                        )}
                        {med.frecuencia && (
                          <Chip size="small" label={`🕐 ${med.frecuencia}`}
                            sx={{ height: 19, fontSize: 10, background: '#fef3c7', color: '#a16207', fontWeight: 600 }} />
                        )}
                        {med.duracion && (
                          <Chip size="small" label={`⏳ ${med.duracion}`}
                            sx={{ height: 19, fontSize: 10, background: '#dcfce7', color: '#15803d', fontWeight: 600 }} />
                        )}
                      </Box>
                      {/* Línea 3 — indicaciones específicas */}
                      {med.indicaciones && (
                        <Typography sx={{ fontSize: 11, color: '#475569', fontStyle: 'italic', lineHeight: 1.4, mb: 0.3 }}>
                          {med.indicaciones}
                        </Typography>
                      )}
                      {/* Línea 4 — razón clínica */}
                      {med.razon && (
                        <Typography sx={{
                          fontSize: 10.5, color: '#7c3aed', mt: 0.2,
                          pl: 0.7, borderLeft: '2px solid #ddd6fe',
                        }}>
                          {med.razon}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* INDICACIONES AL PACIENTE SUGERIDAS */}
          {data.indicaciones_generales && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.7, gap: 1 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 800, color: '#0c4a6e',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  📝 Indicaciones al paciente sugeridas
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={aplicarIndicaciones}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: 11,
                    borderRadius: 1.5, px: 1.3, py: 0.4, flexShrink: 0,
                    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #0e7490 0%, #0891b2 100%)' },
                  }}
                >
                  {indicacionesActuales?.trim()
                    ? 'Agregar al final (Receta)'
                    : 'Llenar Indicaciones al paciente (Receta)'}
                </Button>
              </Box>
              <Box sx={{
                p: 1, borderRadius: 1.5,
                background: '#fff', border: '1px solid #a5f3fc',
                borderLeft: '3px solid #06b6d4',
              }}>
                <Typography sx={{ fontSize: 12, color: '#0f172a', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {data.indicaciones_generales}
                </Typography>
              </Box>
            </Box>
          )}

          {(!data.medicamentos?.length && !data.indicaciones_generales && !data.plan_tratamiento_texto) && (
            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic', textAlign: 'center', py: 1 }}>
              La IA no encontró sugerencias terapéuticas claras. Verificá que haya diagnósticos registrados.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default ConsultaForm;
